import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { client } from "@/lib/api";
import { useAuth } from "@/components/AuthProvider";
import { IKO, IKO_KEYS, wordCount, formatEAT } from "@/lib/design";
import { IkoChip } from "@/components/IkoChip";
import { SourceBadge } from "@/components/SourceBadge";
import { ConfidenceBar } from "@/components/ConfidenceBar";
import { OrganiserCombobox, VenueCombobox } from "@/components/Combobox";
import { QUEUE } from "@/constants/testIds";
import { toast } from "sonner";
import { confirm as confirmDialog } from "@/lib/dialog";
import ManualCropEditor from "@/components/admin/ManualCropEditor";
import PatchToolPanel from "@/components/admin/PatchToolPanel";
import PosterEditor from "@/components/admin/PosterEditor";
import { coverSrc } from "@/lib/coverSrc";
import { PastedCoverPreview } from "@/components/admin/PastedCoverPreview";
import { ChainDesk, ChainField } from "@/components/admin/ChainDesk";
import { QueueFilterRow } from "@/components/admin/QueueFilterRow";
import { IngestMatchDialog } from "@/components/admin/IngestMatchDialog";
import { ConfirmDialog, PromptDialog } from "@/components/admin/ConfirmDialog";
import { SafariDetailsPanel } from "@/components/admin/SafariDetailsPanel";
const emptyDraft = {
  title: "", start_at: "", end_at: "", venue_display: "", venue_id: null,
  price: "", ticket_link: "", description: "", organiser_handle: "",
  co_organiser_handles: [], iko: null, cover_image_url: "",
  slots: [], additional_dates: [],
  country: "KE",
  registration_required: false,
  // WW-158 (Vania 13 Oct · D-4) · unified "how to get in" block.
  // `type` ∈ {ticket_link, registration_link, contact, walk_in}.
  how_to_get_in: { type: "ticket_link", link: "", contact_name: "",
                   contact_phone: "", contact_email: "" },
};

// W-B.R26.1 · country options rendered in the detail form select.
// Codes are ISO-3166-1 alpha-2.  Any option other than "KE" holds the
// row out of Approve (see server-side `gates_pass` + /approve).
const COUNTRY_OPTIONS = [
  { code: "KE", label: "Kenya" },
  { code: "UG", label: "Uganda" },
  { code: "TZ", label: "Tanzania" },
  { code: "RW", label: "Rwanda" },
  { code: "ET", label: "Ethiopia" },
  { code: "ZA", label: "South Africa" },
  { code: "NG", label: "Nigeria" },
  { code: "GH", label: "Ghana" },
  { code: "BE", label: "Belgium" },
  { code: "GB", label: "United Kingdom" },
  { code: "US", label: "United States" },
  { code: "CI", label: "Côte d'Ivoire" },
  { code: "OTHER", label: "Other" },
];
const countryLabel = (code) => {
  const o = COUNTRY_OPTIONS.find((x) => x.code === (code || "KE").toUpperCase());
  return o ? o.label : (code || "Kenya");
};

const normaliseHandle = (h) => (h || "").trim().replace(/^@+/, "");

// WW-213 · "starts in N days" on each queue card, counted in Nairobi days.
function startsIn(iso) {
  if (!iso) return "no date";
  const day = (d) => new Date(new Date(d).toLocaleString("en-US", { timeZone: "Africa/Nairobi" })).setHours(0, 0, 0, 0);
  const n = Math.round((day(iso) - day(Date.now())) / 86400000);
  if (n === 0) return "starts today";
  if (n === 1) return "starts tomorrow";
  if (n > 1) return `starts in ${n} days`;
  return n === -1 ? "started yesterday" : `started ${-n} days ago`;
}

export default function ReviewQueue() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  // crop button hotfix follow-up (Vania 7 Oct 2026 · 13:40 UTC · live
  // QA) · the review queue was silently capped at 200 and the header
  // read "1 of 200" even when the backend had 212 matching rows.
  // Carry the real total so the header can read "1 of 200 (212 total,
  // waiting for review)".
  const [pendingTotal, setPendingTotal] = useState(null);
  const [pendingLimit, setPendingLimit] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);  const [venues, setVenues] = useState([]);
  const [saving, setSaving] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [ingestMatch, setIngestMatch] = useState(null);
  const [ingestText, setIngestText] = useState("");
  const [showIngest, setShowIngest] = useState(false);
  // Bulk selection — founder Msg 71 Part 6
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [lastClickedId, setLastClickedId] = useState(null);
  // R14 → R16 · patchActive is kept so legacy inline tests remain
  // valid, but the real editor-open signal is editorOpen · it drives
  // the filter row hide + three-col takeover below.
  const [patchActive, setPatchActive] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTab, setEditorTab] = useState("crop");
  // Merge view state
  const [mergeView, setMergeView] = useState(null); // { child, parent, diffs }
  // Filter + sort — founder Msg 71 Part 6
  const [filterIko, setFilterIko] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [filterGate, setFilterGate] = useState("all");
  const [filterDup, setFilterDup] = useState("all");
  // WW-157 min (Vania 13 Oct · D-4) · soonest-first is the new
  // default sort. "confidence" removed from the UI; the three choices
  // are soonest / newest ingested / oldest ingested.
  const [sortKey, setSortKey] = useState("start");
  // Wave 18 (Vania 4 Oct) · filter Joy's Drive-screenshot pipeline by
  // captured_by + platform. "all" means no filter.
  const [filterCapturedBy, setFilterCapturedBy] = useState("all");
  const [filterPlatform, setFilterPlatform] = useState("all");
  // Wave 18b.5 D62 stricter (Vania 5 Oct evening) · manual crop editor.
  const [showManualCrop, setShowManualCrop] = useState(false);
  // W-C.45 (Vania 9 Oct · live) · visible saved-crop confirmation so staff
  // see the manual crop landed even if the toast was missed.
  const [savedFlash, setSavedFlash] = useState(null);
  // W-C.46 (Vania 9 Oct · live perf) · the list used to render up to 200
  // cards at once · each fired its thumbnail request immediately and the
  // slowest finished ~29s in. Default page size 30, "show more" grows by
  // another 30, lazy-loaded <img>, 160px thumbnails for admin raw blobs.
  const [visibleCount, setVisibleCount] = useState(30);
  // W-B.R1 (Vania 9 Oct · DL-17 v2) · under 1280 the list becomes a slide
  // over drawer opened by a hamburger in the top bar.
  const [listDrawerOpen, setListDrawerOpen] = useState(false);
  // W-B.R8 (Vania 9 Oct · after Claude JS QA) · track viewport width
  // so we can switch between mobile (<768 single-panel), tablet
  // (768-1279 list is slide-over), desktop (1280+ three columns).
  const [vw, setVw] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1440));
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // W-B.R9 · list stays a slide-over below 1280 (xl); work-area +
  // details sit side-by-side from 1024 (lg) upward.  Below 1024
  // (phone + small tablet) details stacks under the work area but
  // each panel owns its own scroll so the footer stays reachable.
  const isMobile = vw < 768;
  const isTablet = vw >= 768 && vw < 1280;
  const isDesktop = vw >= 1280;
  const isListInline = vw >= 1280;
  const isListDrawer = vw >= 768 && vw < 1280;
  const isRowSideBySide = vw >= 1024;
  // W-B.R6 · live "needs crop" count for the filter chip. Polled on load
  // and after each manual crop save. No sweep, no vision call.
  const [needsCropCount, setNeedsCropCount] = useState(null);
  const [filterNeedsCrop, setFilterNeedsCrop] = useState(false);
  // W-B.R21 · filter chip for rows whose poster is still not confirmed
  // (no_poster + needs_work + not_checked). Backed by the server's
  // `poster_not_confirmed=true` query so list length == count.
  const [filterPosterNotChecked, setFilterPosterNotChecked] = useState(false);
  // WW-152 fix 2 (Vania 13 Oct · D-2 QA FAIL 1) · "show past events" toggle.
  // When ON the server returns ONLY pending rows whose last moment is
  // past, so Vania can approve them into "been there?". When OFF the
  // default upcoming filter stands.
  const [filterPastEvents, setFilterPastEvents] = useState(false);
  // WW-156 (Vania 13 Oct · D-3) · "year looks wrong" filter chip.
  // Sends year_wrong_only=true to the server so Vania can work the
  // 2024/2025 cluster through on its own tab.
  const [filterYearWrong, setFilterYearWrong] = useState(false);
  const [yearWrongCount, setYearWrongCount] = useState(null);
  // WW-154 · the desk header "dup" count must match purge-preview's
  // duplicates_total (not crop-sweep's duplicate_waiting).
  const [dupCount, setDupCount] = useState(null);
  // W-B.R21 · per-state counts come back from the server on every
  // loadList so the chip label and tooltip can show the split.
  const [posterStateCounts, setPosterStateCounts] = useState(null);
  // W-C.51 (Vania 9 Oct · live) · filter rows whose saved poster still
  // has a social app icon in a corner. Driven by overlay_corners on
  // the row (populated by POST /admin/drive-watcher/overlay-scan/{id}).
  const [filterOverlay, setFilterOverlay] = useState(false);
  // W-C.47 (Vania 9 Oct · live) · when the cover URL 404s, flip to the
  // raw screenshot preview so Vania can crop what she sees. Reset on
  // row change so a prior row's error state does not stick.
  const [coverErrored, setCoverErrored] = useState(false);
  const [rawState, setRawState] = useState("loading"); // WW-147.1 · loading | ok | failed
  // crop button hotfix (Vania 7 Oct 2026 · 11:20 UTC · live QA) ·
  // server-managed sweep. One press → POST /crop-sweep/start → poll
  // /status every 2.5 s. Button advertises count from /crop-sweep/count
  // so staff see what pressing it means. In-page confirm, in-page
  // error line. No native dialogs anywhere in the admin desk.
  const [cropping, setCropping] = useState(false);          // button disabled while running
  const [cropStatus, setCropStatus] = useState(null);        // live sweep doc
  const [cropWaitingCount, setCropWaitingCount] = useState(null); // {total_waiting, …}
  // Wave C (Vania 7 Oct 2026) · auto crop is now OFF by default. The admin
  // desk reads the live flag from /admin/drive-watcher/health and flips the
  // "crop waiting posters" button copy accordingly.
  const [cropPipelineEnabled, setCropPipelineEnabled] = useState(null);
  const [cropError, setCropError] = useState("");            // in-page error
  const [cropConfirmOpen, setCropConfirmOpen] = useState(false);
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkDupConfirmOpen, setBulkDupConfirmOpen] = useState(false);
  const [singleRejectOpen, setSingleRejectOpen] = useState(false);
  const fileRef = useRef(null);

  // WW-151 (Vania 13 Oct) · ref mirrors selectedId so loadDetail and
  // loadList can refuse stale responses without having to re-create the
  // callback on every change (and re-triggering effects).
  const selectedIdRef = useRef(null);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

  // Server data → filtered list
  const filteredPending = useMemo(() => {
    let list = [...pending];
    if (filterIko !== "all") list = list.filter((p) => p.iko === filterIko);
    if (filterSource !== "all") list = list.filter((p) => (p.source || "manual") === filterSource);
    if (filterCapturedBy !== "all") list = list.filter((p) => (p.captured_by || "") === filterCapturedBy);
    if (filterPlatform !== "all") list = list.filter((p) => (p.platform || "") === filterPlatform);
    if (filterDup === "dup") list = list.filter((p) => p.dedupe_status === "duplicate");
    if (filterDup === "unique") list = list.filter((p) => p.dedupe_status !== "duplicate");
    if (filterGate !== "all") {
      list = list.filter((p) => {
        const g = p.gates || {};
        if (filterGate === "ready") return p.gates_pass;
        return g[filterGate] === false;
      });
    }
    // W-B.R6 · "needs crop" quick filter. Matches poster_drop / joy rows
    // without a usable cover · same discriminator as W-C.46 (d).
    if (filterNeedsCrop) {
      list = list.filter((p) => {
        const src = p.cover_source || "";
        const capturedBy = p.captured_by || "";
        return (capturedBy === "poster_drop" || capturedBy === "joy")
          && (!src || src === "needs_recrop" || src === "skipped");
      });
    }
    if (filterOverlay) {
      list = list.filter((p) => Array.isArray(p.overlay_corners) && p.overlay_corners.length > 0);
    }
    if (sortKey === "newest") list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    if (sortKey === "oldest") list.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    // WW-157 sort fix (Vania 13 Oct · D-4 QA) · "soonest event first"
    // was putting every row with no start_at at the top because "" <
    // any real ISO string. Push nulls to the bottom so dated rows
    // lead the list in every "soonest" view.
    // WW-213 · soonest upcoming first, then events already running, then no date.
    if (sortKey === "start") {
      const rank = (p) => (!(p.start_at || "").trim() ? 2 : (Date.parse(p.start_at) || Infinity) < Date.now() ? 1 : 0);
      list.sort((a, b) => rank(a) - rank(b) || (a.start_at || "").localeCompare(b.start_at || ""));
    }
    if (sortKey === "confidence") list.sort((a, b) => (b.iko_confidence || 0) - (a.iko_confidence || 0));
    return list;
  }, [pending, filterIko, filterSource, filterGate, filterDup, filterCapturedBy, filterPlatform, filterNeedsCrop, filterOverlay, sortKey]);

  const overlayCount = useMemo(
    () => pending.filter((p) => Array.isArray(p.overlay_corners) && p.overlay_corners.length > 0).length,
    [pending],
  );
  const anyQueueFilter = filterIko !== "all" || filterSource !== "all" || filterCapturedBy !== "all"
    || filterPlatform !== "all" || filterDup !== "all" || filterGate !== "all" || filterNeedsCrop
    || filterOverlay || filterPosterNotChecked || filterPastEvents || filterYearWrong;

  const sourceKeys = useMemo(
    () => Array.from(new Set(pending.map((p) => p.source || "manual"))).sort(),
    [pending],
  );

  // W-C.46 · reset the "show more" window when the filter / sort changes
  // so a new filter doesn't start at the 90th card.
  useEffect(() => { setVisibleCount(30); },
    [filterIko, filterSource, filterCapturedBy, filterPlatform, filterDup, filterGate, filterNeedsCrop, filterOverlay, sortKey]);

  // W-C.47 · reset the per-row cover error state when the selected row
  // changes so a failed cover from one row doesn't flip the next row
  // to the raw-screenshot path by accident.
  useEffect(() => { setCoverErrored(false); setRawState("loading"); }, [selectedId, detail?.cover_image_url]);

  // W-B.R6 · needs-crop count helper. Falls back to a local scan of the
  // already-loaded pending list so the chip is useful even if the backend
  // endpoint is unreachable. The server number trumps the local one when
  // present.
  const localNeedsCropCount = useMemo(() => pending.filter((p) => {
    const src = p.cover_source || "";
    const capturedBy = p.captured_by || "";
    return (capturedBy === "poster_drop" || capturedBy === "joy")
      && (!src || src === "needs_recrop" || src === "skipped");
  }).length, [pending]);
  const needsCropLabel = typeof needsCropCount === "number" ? needsCropCount : (pendingTotal === null ? null : localNeedsCropCount);

  const loadSeqRef = useRef(0);
  const [listLoading, setListLoading] = useState(false);
  const [waitingTotal, setWaitingTotal] = useState(null);
  const [counts, setCounts] = useState(null); // WW-199 · upcoming waiting · past, still pending · archived · hidden duplicates
  const inflightRef = useRef("");
  const [loadedKey, setLoadedKey] = useState(""); // WW-199.1.b · which filters the shown list belongs to
  const loadList = useCallback(async () => {
    // WW-199.1.a · the past view loads every past row so "N match" equals the past count.
    const q = new URLSearchParams({ status: "pending", limit: filterPastEvents ? "5000" : "500" });
    if (filterPosterNotChecked) q.set("poster_not_confirmed", "true");
    // WW-152 fix 2 · past-events toggle sends `past_only=true` so only
    // past pending rows come back · default list stays upcoming-only.
    if (filterPastEvents) q.set("past_only", "true");
    if (filterYearWrong) q.set("year_wrong_only", "true");
    const key = q.toString();
    // WW-199.1.b · the same list is never asked for twice at once.
    if (inflightRef.current === key) return;
    inflightRef.current = key;
    // WW-157 · only the newest request may paint the list (fast tabs vs a slow default list).
    const seq = ++loadSeqRef.current;
    setListLoading(true);
    const r = await client.get(`/admin/queue?${key}`).finally(() => {
      if (inflightRef.current === key) inflightRef.current = "";
      if (seq === loadSeqRef.current) setListLoading(false);
    });
    if (seq !== loadSeqRef.current) return;
    setLoadedKey(key);
    setPending(r.data.pending || []);
    setPendingTotal(typeof r.data.total_matching === "number" ? r.data.total_matching : null);
    if (typeof r.data.waiting_total === "number") setWaitingTotal(r.data.waiting_total);
    if (r.data.counts) setCounts(r.data.counts);
    setPendingLimit(typeof r.data.limit === "number" ? r.data.limit : null);
    if (typeof r.data.year_wrong_count === "number") setYearWrongCount(r.data.year_wrong_count);
    // WW-154 · pull purge-preview's duplicates_total so the header dup
    // count agrees with the purge preview exactly.  Non-blocking · if
    // it fails we leave the previous value.
    client.get("/admin/duplicates/purge-preview").then((pp) => {
      const n = pp?.data?.summary?.duplicates_total;
      const del = pp?.data?.summary?.would_delete_count;
      // WW-197 · same two numbers as the Ops panel: hidden duplicates and how many apply would delete.
      if (typeof n === "number") setDupCount(typeof del === "number" ? `${n} hidden duplicates · ${del} to delete` : `${n} hidden duplicates`);
    }).catch(() => {});
    // W-B.R21 · pick up poster_state_counts from the server
    if (r.data.poster_state_counts) setPosterStateCounts(r.data.poster_state_counts);
    // WW-150 (Vania 13 Oct) · the auto-pick must sit inside the current
    // filtered view, not the full `pending` array · otherwise the card
    // never highlights (details show SAMINT but no card is active).
    const items = r.data.pending || [];
    const nextSelected = selectedIdRef.current && items.find((p) => p.id === selectedIdRef.current)
      ? selectedIdRef.current
      : (items[0]?.id || null);
    if (nextSelected && nextSelected !== selectedIdRef.current) {
      setSelectedId(nextSelected);
    }
  }, [filterPosterNotChecked, filterPastEvents, filterYearWrong]);

  // ROUND TWO Part A — progress counter + prev/next helpers
  const currentIndex = useMemo(
    () => filteredPending.findIndex((p) => p.id === selectedId),
    [filteredPending, selectedId],
  );
  const goNext = () => {
    if (!filteredPending.length) return;
    const nx = currentIndex < 0 ? 0 : Math.min(filteredPending.length - 1, currentIndex + 1);
    setSelectedId(filteredPending[nx].id);
  };
  const goPrev = () => {
    if (!filteredPending.length) return;
    const pv = currentIndex <= 0 ? 0 : currentIndex - 1;
    setSelectedId(filteredPending[pv].id);
  };
  // W-B.R6 (Vania 9 Oct) · "n" jumps to the next row that still needs a
  // crop (poster_drop / joy with no good cover) · after saving a crop,
  // the onSaved callback also fires this to keep the keyboard flow
  // going. Wraps to the beginning if the end is reached.
  const goNextNeedsCrop = () => {
    if (!filteredPending.length) return;
    const needsCrop = (p) => {
      const src = p.cover_source || "";
      const capturedBy = p.captured_by || "";
      return (capturedBy === "poster_drop" || capturedBy === "joy")
        && (!src || src === "needs_recrop" || src === "skipped");
    };
    const start = currentIndex === -1 ? 0 : currentIndex + 1;
    for (let step = 0; step < filteredPending.length; step++) {
      const p = filteredPending[(start + step) % filteredPending.length];
      if (p.id === selectedId) continue;
      if (needsCrop(p)) { setSelectedId(p.id); return; }
    }
    toast.info("no more rows need a crop");
  };
  const clearSelection = () => { setSelectedId(null); setDetail(null); };

  const loadVenues = useCallback(async () => {
    try {
      const r = await client.get("/admin/venues");
      setVenues(r.data.venues || []);
    } catch {}
  }, []);

  const loadDetailReqId = useRef(0);
  // WW-150/151 fix 4 (Vania 13 Oct · D-2 QA FAIL 2) · a card tap sets
  // selectedId immediately, but the GET /admin/queue/:id round-trip
  // takes ~1.3s. During that window `detail` still points at the
  // previous row. If an operator presses "poster looks good" during
  // the load, the handler sends the PREVIOUS row's id. The right fix
  // is simple: expose a `detailLoading` flag that drives a
  // "detailMatchesSelection" boolean · every action button in details
  // disables itself while they don't match.
  const [detailLoading, setDetailLoading] = useState(false);
  // WW-198 · thumbnails wait for the first row details so they never hold up that request.
  const [thumbsReady, setThumbsReady] = useState(false);
  const [draftCoverFailed, setDraftCoverFailed] = useState(false); // WW-174.a
  const [chainsOpen, setChainsOpen] = useState(false); // WW-173 · chain desk column
  const [chainTicked, setChainTicked] = useState([]);
  useEffect(() => {
    if (thumbsReady || pendingTotal === null) return undefined;
    const t = setTimeout(() => setThumbsReady(true), 2500);
    return () => clearTimeout(t);
  }, [thumbsReady, pendingTotal]);
  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); setDetailLoading(false); return; }
    loadDetailReqId.current += 1;
    const myReq = loadDetailReqId.current;
    setDetailLoading(true);
    try {
      const r = await client.get(`/admin/queue/${id}`);
      if (myReq !== loadDetailReqId.current) return;
      if (id !== selectedIdRef.current) return;
      setDetail(r.data.pending);
      const p = r.data.pending;
      setDraft({
        title: p.title || "", start_at: p.start_at || "", end_at: p.end_at || "",
        venue_display: p.venue_display || "", venue_id: p.venue_id || null,
        price: p.price || "", ticket_link: p.ticket_link || "",
        description: p.description || "",
        organiser_handle: normaliseHandle(p.organiser_handle),  iko: p.iko || null,
        co_organiser_handles: Array.isArray(p.co_organiser_handles)
          ? p.co_organiser_handles.map(normaliseHandle).filter(Boolean)
          : [],
        cover_image_url: p.cover_image_url || "",
        slots: Array.isArray(p.slots) ? p.slots : [],
        additional_dates: Array.isArray(p.additional_dates) ? p.additional_dates : [],
        country: (p.country || "KE").toUpperCase(),
        safari: p.safari || null,
        registration_required: !!p.registration_required,
        how_to_get_in: p.how_to_get_in && p.how_to_get_in.type
          ? { type: p.how_to_get_in.type || "ticket_link",
              link: p.how_to_get_in.link || "",
              contact_name: p.how_to_get_in.contact_name || "",
              contact_phone: p.how_to_get_in.contact_phone || "",
              contact_email: p.how_to_get_in.contact_email || "" }
          : { type: p.registration_required ? "registration_link" : "ticket_link",
              link: p.ticket_link || "",
              contact_name: "", contact_phone: "", contact_email: "" },
      });
    } finally {
      if (myReq === loadDetailReqId.current) setDetailLoading(false);
      setThumbsReady(true);
    }
  }, []);

  useEffect(() => { loadList(); loadVenues(); }, [loadList, loadVenues]);
  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  // Msg 238 · presence + realtime queue sync.
  const [presence, setPresence] = useState({ on_desk: [], locks: {} });
  useEffect(() => {
    const beat = () => {
      client.post("/admin/presence/heartbeat", { pending_id: selectedId })
        .catch(() => {});
    };
    beat();
    const b = setInterval(beat, 30000);
    return () => clearInterval(b);
  }, [selectedId]);
  useEffect(() => {
    const tick = async () => {
      try {
        // WW-150 (Vania 13 Oct) · reuse loadList so the periodic refresh
        // keeps the same 500-row cap and the server-side filters that
        // the initial load picked. Previously this tick fired
        // `/admin/queue` with no params, which snapped the list back to
        // the 200 default and dropped 38 of the 238 waiting rows every
        // 12s.
        await Promise.all([
          loadList(),
          client.get("/admin/presence/active").then((p) => setPresence(p.data)),
        ]);
      } catch {}
    };
    const t = setInterval(tick, 12000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList]);

  // When the selected row disappears (someone else published/rejected it),
  // auto-advance to the next queue card so nobody stares at a stale one.
  useEffect(() => {
    if (!selectedId) return;
    if (!pending.length) { setDetail(null); return; }
    if (!pending.find((p) => p.id === selectedId)) {
      const nextId = filteredPending[0]?.id || null;
      setSelectedId(nextId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  // When detail says the row is no longer pending, tell the admin and move on.
  useEffect(() => {
    if (!detail) return;
    if (detail.status && detail.status !== "pending") {
      const who = detail.reviewer_first_name ? ` by ${detail.reviewer_first_name}` : "";
      toast.error(`already ${detail.status}${who} · moving on`);
      const nextId = filteredPending[currentIndex + 1]?.id || null;
      setSelectedId(nextId); setDetail(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  // Keyboard-first review — founder ROUND TWO Part A
  // j/k next/prev · a approve · r reject · s save · d merge (if dup) ·
  // n jump to the next row that still needs a crop (W-B.R6) · Esc back
  // to list
  useEffect(() => {
    const onKey = (e) => {
      // Skip when a form control has focus
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key === "j") { e.preventDefault(); goNext(); }
      else if (key === "k") { e.preventDefault(); goPrev(); }
      else if (key === "n") { e.preventDefault(); goNextNeedsCrop(); }
      else if (key === "escape") { e.preventDefault(); clearSelection(); setListDrawerOpen(false); }
      else if (selectedId && !saving) {
        if (key === "a" && gatesPass) { e.preventDefault(); approve(); }
        else if (key === "r") { e.preventDefault(); reject(); }
        else if (key === "s") { e.preventDefault(); save(); }
        else if (key === "d" && detail?.dedupe_parent_id) { e.preventDefault(); mergeWithParent(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    /* eslint-disable-next-line */
  }, [selectedId, saving, pending, currentIndex, detail, draft]);

  const gates = useMemo(() => {
    const wc = wordCount(draft.description);
    const isPaid = !!draft.price && draft.price.trim().toLowerCase() !== "free";
    // WW-158 fix 1 (Vania 13 Oct · D-4 QA) · "how to get in" gate
    // computed LIVE from the draft so Approve mirrors the backend
    // `_htg_pass` (routers/pending.py:205-217). Previously the UI only
    // read `detail.gates.how_to_get_in_pass`, which was the state AT
    // LOAD, so clearing the contact fields and pressing Approve first
    // saved the broken draft and only then rejected it.
    const htg = draft.how_to_get_in || {};
    const htgType = (htg.type || "").trim();
    const htgLink = (htg.link || draft.ticket_link || "").trim();
    const htgPhone = (htg.contact_phone || "").trim();
    const htgEmail = (htg.contact_email || "").trim();
    const phoneRe = /^(?:\+?254\s*|0)(?:7|1)\d{2}[\s\-.]?\d{3}[\s\-.]?\d{3}$/;
    const emailRe = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;
    const htgPhoneOk = !!htgPhone && phoneRe.test(htgPhone);
    const htgEmailOk = !!htgEmail && emailRe.test(htgEmail);
    let htgPass = false;
    let htgReason = "";
    if (htgType === "walk_in") {
      htgPass = !isPaid;
      htgReason = htgPass ? "" : "just show up only works on free events · change price or pick a path";
    } else if (htgType === "ticket_link" || htgType === "registration_link") {
      htgPass = !!htgLink;
      htgReason = htgPass ? "" : (htgType === "registration_link"
        ? "how to get in: add the registration link"
        : "how to get in: add a ticket link");
    } else if (htgType === "contact") {
      htgPass = htgPhoneOk || htgEmailOk;
      if (!htgPass) {
        if (htgPhone && !htgPhoneOk) htgReason = "how to get in: phone is not a kenyan number · 07xx / 01xx / +254";
        else if (htgEmail && !htgEmailOk) htgReason = "how to get in: email is not valid";
        else htgReason = "how to get in: add a phone or email";
      }
    } else {
      // No explicit type · legacy pass path (free event with nothing is
      // an implicit walk-in; paid event needs a legacy ticket_link).
      htgPass = isPaid ? !!(draft.ticket_link || "").trim() : true;
      htgReason = htgPass ? "" : "how to get in: pick a path (ticket link, registration link, contact or just show up)";
    }
    return {
      iko: !!draft.iko,
      start: !!draft.start_at,
      venue: !!(draft.venue_id || draft.venue_display),
      price: !!draft.price,
      cover: !!draft.cover_image_url,
      description: wc >= 40,
      organiser: !!draft.organiser_handle,
      ticket: !isPaid || !!draft.ticket_link,
      htgPass,
      htgReason,
      isPaid,
      wordCount: wc,
    };
  }, [draft]);

  const gatesPass = gates.iko && gates.start && gates.venue && gates.price && gates.cover && gates.description && gates.organiser && gates.ticket && gates.htgPass;

  const save = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await client.put(`/admin/queue/${selectedId}`, draft);
      toast.success("Draft saved");
      await loadDetail(selectedId);
      await loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // Wave 18b.5 bug batch v2 (Vania 6 Oct) · derive "cover ready" state so
  // the approve button and the "needs a poster" chip stay in sync with the
  // server-side approve-gate.
  const coverReady = useMemo(() => {
    if (!detail) return false;
    const url = (detail.cover_image_url || "").trim();
    const src = (detail.cover_source || "").toLowerCase();
    if (!url) return false;
    // W-B.R25.1 · bare drive-watcher blobs are raw uncropped drive files
    // and still need cropping · reject them.  Public cover-blob URLs
    // are post-crop/post-patch outputs so they count as a real poster.
    if (url.includes("/admin/drive-watcher/blob/")) return false;
    if (["needs_recrop", "skipped"].includes(src)) return false;
    // W-B.R25.1 · accept every human-approved cover shape: crop, patch,
    // paste, upload, generic manual.  Previously only clean_poster /
    // bbox_crop / manual_crop were counted, so a patched poster
    // (manual_patch) or pasted link (manual_paste) was being flagged
    // as "needs a poster".
    if (["clean_poster", "bbox_crop", "manual_crop", "manual_patch",
         "manual_paste", "manual_upload", "manual"].includes(src)) return true;
    return /^https?:\/\//i.test(url);
  }, [detail]);
  // WW-158 fix 1 · also require the live-draft how-to-get-in gate so
  // the button cannot be pressed while the current form state would
  // fail on the server.
  const canApprove = Boolean(detail?.gates_pass) && coverReady && Boolean(detail?.poster_confirmed) && (String(detail?.country || "KE").toUpperCase() === "KE") && gates.htgPass;
  // W-B.R25.1 · map gate state to the SAME copy as the queue card chip
  // and the Approve-blocked panel ("poster not checked") so the Approve
  // tooltip never says one thing while the panel below it says another.
  const posterStateCopy = useMemo(() => {
    const st = detail?.poster_gate?.state;
    if (st === "no_poster") return "needs a poster · crop or paste a cover link";
    if (st === "needs_work") return "poster needs work";
    if (st === "not_checked") return "poster not checked";
    return detail?.poster_gate?.label || "poster not checked";
  }, [detail]);
  // WW-151 (Vania 13 Oct) · Approve-blocker list comes from the SAME
  // `gates` object the server uses for /approve, so a row cannot read
  // "ready to publish" or "add a ticket link (paid event)" while the
  // real blocker is price_missing. The client no longer invents reasons
  // from a parallel useMemo over `draft`.
  const serverGates = detail?.gates || {};
  const approveBlockedReasons = useMemo(() => {
    if (!detail) return [];
    const g = detail.gates || {};
    const out = [];
    // WW-158 fix 4 (Vania 13 Oct · D-4 QA) · when the live draft has
    // already fixed a gate that the saved row still fails on, HIDE the
    // stale server line and nudge Vania to save the draft so the checks
    // refresh. Previously a draft with a valid contact kept showing
    // "add a ticket link · paid event" because the saved row was still
    // walk-in / empty.
    let draftFixedSomething = false;
    const noteServerFailDraftFixed = () => { draftFixedSomething = true; };
    // Poster · poster_confirmed stays as-is (needs a save+confirm cycle anyway).
    if (!coverReady) out.push("needs a poster · crop or paste a cover link");
    else if (!detail.poster_confirmed) out.push(posterStateCopy);
    if (!g.has_iko) {
      if (gates.iko) noteServerFailDraftFixed();
      else out.push("pick an iko");
    }
    if (!g.has_start) {
      if (gates.start) noteServerFailDraftFixed();
      else out.push("add a start time");
    }
    if (!g.has_venue) {
      if (gates.venue) noteServerFailDraftFixed();
      else out.push("add a venue");
    }
    const draftPriceReal = !!draft.price && draft.price.trim().toLowerCase() !== "price to confirm";
    if (g.price_missing) {
      if (draftPriceReal) noteServerFailDraftFixed();
      else out.push("price to confirm · set a price or mark it free");
    } else if (!g.has_price) {
      if (gates.price) noteServerFailDraftFixed();
      else out.push("add a price");
    }
    if (!g.has_description) {
      if (gates.description) noteServerFailDraftFixed();
      else out.push(`description is ${g.description_word_count || 0} of ${g.description_min || 40} words`);
    }
    if (!g.has_organiser) {
      if (gates.organiser) noteServerFailDraftFixed();
      else out.push("add a curator handle · never a ticketing platform");
    }
    if (g.is_paid && !g.has_ticket_link_if_paid) {
      // WW-158 fix 4 · the live htg gate supersedes this server line.
      if (gates.htgPass) noteServerFailDraftFixed();
      else out.push("add a ticket link · paid event");
    }
    // WW-153 · "registration required" + no link → explicit copy.
    if (g.registration_missing_link) {
      if (gates.htgPass) noteServerFailDraftFixed();
      else out.push("registration required: add the registration link");
    }
    // WW-158 fix 1 (Vania 13 Oct · D-4 QA) · live-draft how-to-get-in
    // reason so the footer tells Vania the real blocker the moment
    // she clears a field, instead of waiting for a save round-trip.
    if (!gates.htgPass && gates.htgReason) {
      out.push(gates.htgReason);
    }
    if (detail.country && String(detail.country).toUpperCase() !== "KE") {
      out.push("outside Kenya · held for the special tab");
    }
    // Server already filters "past event" out of blocking_notes_for_approve
    // (WW-152 · past events can publish and go into "been there?").
    (g.blocking_notes_for_approve || []).forEach((n) => out.push(String(n).trim()));
    // WW-158 fix 4 · tell Vania to save when the draft is strictly
    // ahead of the saved row. Only when there are no OTHER live
    // blockers above, otherwise the save prompt is noise.
    if (draftFixedSomething && out.length === 0) {
      out.push("save the draft to update the checks");
    }
    return out;
  }, [detail, coverReady, posterStateCopy, gates.htgPass, gates.htgReason, gates.iko, gates.start, gates.venue, gates.price, gates.description, gates.organiser, draft.price]);
  const approveBlockedReason = approveBlockedReasons.join(" · ");

  const approve = async () => {
    if (!selectedId) return;
    // WW-158 fix 1 (Vania 13 Oct · D-4 QA) · never save a draft that
    // would fail the how-to-get-in gate.  Previously Approve PUT the
    // draft first and only then saw the 400, which wiped seed H's
    // contact fields.  Short-circuit with the exact reason instead.
    if (!gates.htgPass) {
      toast.error(gates.htgReason || "how to get in: pick a path");
      return;
    }
    setSaving(true);
    // Msg 238 · advance to next BEFORE mutating so the list refresh feels instant.
    const nextId = filteredPending[currentIndex + 1]?.id || null;
    try {
      await client.put(`/admin/queue/${selectedId}`, { ...draft, client_updated_at: detail?.updated_at });
      try {
        await client.post(`/admin/queue/${selectedId}/approve`);
      } catch (ae) {
        const ad = ae?.response?.data?.detail;
        // WW-185 · looks abroad: staff must confirm Kenya, otherwise it stays held.
        if (ae?.response?.status !== 409 || ad?.error !== "outside_kenya_suspected") throw ae;
        if (!(await confirmDialog({ title: "is this in Kenya?", message: ad.message, confirmLabel: "it is in Kenya, publish" }))) {
          toast.message("held · set the country to send it to the safari section");
          return;
        }
        await client.post(`/admin/queue/${selectedId}/approve?confirm_kenya=true`);
      }
      toast.success("event approved · published");
      setSelectedId(nextId);
      setDetail(null);
      await loadList();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.error === "already_reviewed") {
        toast.error(`already ${d.status} · moving on`);
        setSelectedId(nextId); setDetail(null); await loadList();
      } else if (e?.response?.status === 409 && d?.error === "stale_write") {
        toast.error("someone else edited this row · reloading");
        await loadDetail(selectedId);
      } else {
        toast.error(typeof d === "string" ? d : "Cannot approve — gates failed");
      }
    } finally { setSaving(false); }
  };

  const reject = async () => {
    if (!selectedId) return;
    setSingleRejectOpen(true);
  };
  const doReject = async (reason) => {
    setSingleRejectOpen(false);
    if (!selectedId) return;
    setSaving(true);
    const nextId = filteredPending[currentIndex + 1]?.id || null;
    try {
      await client.post(`/admin/queue/${selectedId}/reject`, { reason: reason || "" });
      toast.success("rejected");
      setSelectedId(nextId);
      setDetail(null);
      await loadList();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409) {
        toast.error(`already ${d?.status || "reviewed"} · moving on`);
        setSelectedId(nextId); setDetail(null); await loadList();
      } else { toast.error("reject failed"); }
    } finally { setSaving(false); }
  };

  const mergeWithParent = async () => {
    if (!detail?.dedupe_parent_id) return;
    setSaving(true);
    try {
      await client.post(`/admin/queue/${selectedId}/merge`, {
        keep_id: detail.dedupe_parent_id,
        discard_id: selectedId,
      });
      toast.success("Merged with earlier record");
      setSelectedId(null);
      await loadList();
    } catch { toast.error("Merge failed"); } finally { setSaving(false); }
  };

  const regenerateDescription = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const r = await client.post(`/admin/queue/${selectedId}/regenerate-description`);
      setDraft((d) => ({ ...d, description: r.data.description }));
      toast.success(`Regenerated · ${r.data.word_count} words`);
    } catch { toast.error("Regenerate failed"); } finally { setSaving(false); }
  };

  // Bulk selection helpers
  const toggleSelect = (id, e) => {
    setLastClickedId(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (e && e.shiftKey && lastClickedId) {
        // Shift-click range from lastClickedId to id (within filtered view)
        const ids = filteredPending.map((p) => p.id);
        const i1 = ids.indexOf(lastClickedId);
        const i2 = ids.indexOf(id);
        if (i1 >= 0 && i2 >= 0) {
          const [lo, hi] = i1 < i2 ? [i1, i2] : [i2, i1];
          for (let i = lo; i <= hi; i++) next.add(ids[i]);
          return next;
        }
      }
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllMatching = () => setSelectedIds(new Set(filteredPending.map((p) => p.id)));
  const clearSelection2 = () => setSelectedIds(new Set());

  const bulkReject = async () => {
    if (!selectedIds.size) return;
    setBulkRejectOpen(true);
  };
  // W-B.R21 · bulk approve. Backend returns {approved, approved_ids,
  // skipped, skipped_rows:[{id, reason, poster_gate?}]}. We toast the
  // headline, then push a detailed toast per skipped row with its title
  // from the loaded list so Vania sees exactly who sat out and why.
  const bulkApprove = async () => {
    if (!selectedIds.size) return;
    try {
      const r = await client.post("/admin/queue/bulk/approve", { ids: [...selectedIds] });
      const approved = r.data.approved || 0;
      const skipped = r.data.skipped_rows || [];
      const titleOf = (id) => (pending.find((p) => p.id === id)?.title || id.slice(0, 10) + "…");
      const prettyReason = (reason, gate) => {
        if (reason === "poster_not_confirmed") return gate?.label || "poster not confirmed";
        if (reason === "gates_failed") return "other gates failed";
        if (reason === "not_found") return "not found";
        if ((reason || "").startsWith("already_")) return `already ${reason.slice(8)}`;
        return reason || "unknown";
      };
      toast.success(`approved ${approved} · skipped ${skipped.length}`);
      // Per-row skipped detail · one line per row (keep stacked so Vania
      // can scan). Clamp to the first 10 so a huge selection doesn't
      // spam the UI; add a trailing count line when truncated.
      skipped.slice(0, 10).forEach((row) => {
        toast.error(`skipped · ${titleOf(row.id)} · ${prettyReason(row.reason, row.poster_gate)}`, { duration: 8000 });
      });
      if (skipped.length > 10) {
        toast.error(`… +${skipped.length - 10} more skipped rows (see server response)`, { duration: 8000 });
      }
      clearSelection2();
      await loadList();
    } catch (e) {
      toast.error(`bulk approve failed · ${e?.response?.data?.detail || e?.message || ""}`);
    }
  };
  const doBulkReject = async (reason) => {
    setBulkRejectOpen(false);
    if (!selectedIds.size) return;
    try {
      const r = await client.post("/admin/queue/bulk/reject", { ids: [...selectedIds], reason: reason || "" });
      toast.success(`Rejected ${r.data.rejected} row(s)`);
      clearSelection2();
      await loadList();
    } catch { toast.error("Bulk reject failed"); }
  };
  const bulkMarkDuplicateOf = async () => {
    if (selectedIds.size < 2) return toast.error("Select 2+ rows (last one becomes the parent)");
    setBulkDupConfirmOpen(true);
  };
  const doBulkMarkDuplicateOf = async () => {
    setBulkDupConfirmOpen(false);
    if (selectedIds.size < 2) return;
    const ids = [...selectedIds];
    const parent_id = ids[ids.length - 1];
    const losers = ids.slice(0, -1);
    try {
      const r = await client.post("/admin/queue/bulk/mark-duplicate", { ids: losers, parent_id });
      toast.success(`Merged ${r.data.merged} into parent`);
      clearSelection2();
      await loadList();
    } catch { toast.error("Bulk mark-duplicate failed"); }
  };

  const openMergeView = async () => {
    if (!selectedId) return;
    try {
      const r = await client.get(`/admin/queue/${selectedId}/diff-parent`);
      setMergeView(r.data);
    } catch { toast.error("Failed to load merge view"); }
  };
  const closeMergeView = () => setMergeView(null);

  // WW-172 · a matched ingest opens the existing row and asks update or create anyway.
  const showIngestMatch = (data) => {
    setIngestMatch({ ...data.match, raw_input_id: data.raw_input_id });
    setShowIngest(false);
    if (data.match.matched_kind === "pending") setSelectedId(data.pending_event_id);
    loadList();
  };
  const [creatingAnyway, setCreatingAnyway] = useState(false);
  const createAnyway = async () => {
    setCreatingAnyway(true);
    try {
      const r = await client.post(`/admin/ingest/create-anyway/${ingestMatch.raw_input_id}`);
      toast.success("created a new event");
      setIngestMatch(null);
      if (r.data.pending_event_id) setSelectedId(r.data.pending_event_id);
      await loadList();
    } catch { toast.error("create anyway failed"); } finally { setCreatingAnyway(false); }
  };

  const doTextIngest = async () => {
    if (!ingestText.trim()) return;
    setIngesting(true);
    try {
      const r = await client.post("/admin/ingest/text", { text: ingestText, source: "manual" });
      setIngestText("");
      if (r.data.match) { showIngestMatch(r.data); return; }
      toast.success("Extracted");
      setShowIngest(false);
      if (r.data.pending_event_id) setSelectedId(r.data.pending_event_id);
      await loadList();
    } catch { toast.error("Extraction failed"); } finally { setIngesting(false); }
  };

  const doImageIngest = async (file) => {
    if (!file) return;
    setIngesting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source", "manual");
      const r = await client.post("/admin/ingest/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        validateStatus: () => true,
      });
      if (r.status >= 400) {
        const d = r.data?.detail || r.data || {};
        const msg = d.message || d.error || `upload failed · http ${r.status}`;
        toast.error(msg);
      } else if (!r.data.pending_event_id) {
        toast.error(r.data.message || "accepted · but no event could be extracted");
      } else if (r.data.match) {
        showIngestMatch(r.data);
      } else {
        toast.success(`extracted · ${r.data.mime || "image"} · ${Math.round((r.data.size_bytes || 0) / 1024)} KB`);
        if (r.data.pending_event_id) setSelectedId(r.data.pending_event_id);
        await loadList();
      }
    } catch (e) { toast.error(e?.message || "Extraction failed"); } finally { setIngesting(false); if (fileRef.current) fileRef.current.value=""; }
  };

  // crop button hotfix (Vania 7 Oct 2026 · 11:20 UTC · live QA) ·
  // server-managed sweep: one press → POST /crop-sweep/start → poll
  // /status every 2.5 s. Button text + progress read from the sweep
  // doc the server owns, so a closed tab doesn't stop the job.
  const refreshCropCount = async () => {
    try {
      const r = await client.get("/admin/drive-watcher/crop-sweep/count");
      setCropWaitingCount(r.data || null);
    } catch { /* button just hides the count on failure */ }
  };
  const refreshCropPipelineFlag = async () => {
    try {
      const r = await client.get("/admin/drive-watcher/health");
      setCropPipelineEnabled(!!(r.data && r.data.crop_pipeline_enabled));
    } catch { setCropPipelineEnabled(null); }
  };
  const refreshCropStatus = async () => {
    try {
      const r = await client.get("/admin/drive-watcher/crop-sweep/status");
      const s = r.data || null;
      setCropStatus(s);
      if (s && s.state === "running") {
        setCropping(true);
      } else {
        if (cropping) { toast.success("crop sweep done"); await loadList(); }
        setCropping(false);
      }
      if (s && s.state === "error" && s.error_message) {
        setCropError(s.error_message);
      }
      return s;
    } catch (e) {
      setCropError(`status fetch failed: ${(e && e.message) || "unknown"}`);
      return null;
    }
  };
  // Load count on mount + poll status while running.
  useEffect(() => { refreshCropCount(); refreshCropStatus(); refreshCropPipelineFlag(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    // W-B.R21 FU · poll while running OR while a stale panel is on
    // screen so (a) running sweeps tick "last update Xs ago" and
    // (b) a Stop click or a cleared heartbeat flips the panel off
    // without a manual reload.
    const isStaleOnScreen = cropStatus && cropStatus.state === "stale";
    if (!cropping && !isStaleOnScreen) return undefined;
    const t = setInterval(() => { refreshCropStatus(); }, 1500);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [cropping, cropStatus?.state]);

  const openCropConfirm = () => {
    setCropError("");
    setCropConfirmOpen(true);
  };
  const doStartCropSweep = async () => {
    setCropConfirmOpen(false);
    setCropError("");
    setCropping(true);
    try {
      const r = await client.post("/admin/drive-watcher/crop-sweep/start");
      const body = r.data || {};
      if (body.ok === false && body.error === "already_running") {
        toast.error("a crop sweep is already running");
        setCropStatus(body.sweep || null);
      } else {
        toast.success("crop sweep started");
      }
      await refreshCropStatus();
      await refreshCropCount();
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.detail) || (e && e.message) || "unknown error";
      setCropError(`start failed: ${msg}`);
      toast.error("could not start crop sweep");
      setCropping(false);
    }
  };

  return (
    <div data-testid="review-desk-shell" className="flex h-screen max-h-screen min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      {/* W-B.R9 (Vania 9 Oct 21:05 · after her own JS QA on preview) ·
          the shell locks the page to the viewport height so each inner
          column scrolls on its own.  Admin rail from Layout.jsx stays
          the only sidebar (R8 item 1 amendment). */}
      {/* in-page dialogs (Claude webview suppresses native window.confirm). */}
      <ConfirmDialog
        open={cropConfirmOpen}
        title="crop waiting posters?"
        message={cropWaitingCount
          ? `will crop ${cropWaitingCount.total_waiting} posters that need a crop${cropWaitingCount.parts ? ` · ${cropWaitingCount.parts.to_review} in upcoming waiting · ${cropWaitingCount.parts.past_pending} in past, still pending · ${cropWaitingCount.parts.archived} in archived · ${cropWaitingCount.parts.hidden_duplicates} in hidden duplicates · ${cropWaitingCount.parts.approved_or_rejected} approved or rejected` : ""}. runs on the server, you can close the tab. nothing publishes until staff approve.`
          : "will crop every poster waiting for a crop. runs on the server, you can close the tab. nothing publishes until staff approve."}
        confirmLabel="yes, start the sweep"
        cancelLabel="not yet"
        onConfirm={doStartCropSweep}
        onCancel={() => setCropConfirmOpen(false)}
        testIdPrefix="crop-confirm"
      />
      <PromptDialog
        open={singleRejectOpen}
        title="reject this row?"
        message="optional reason (shown to the submitter)"
        placeholder="why reject?"
        confirmLabel="reject"
        onConfirm={doReject}
        onCancel={() => setSingleRejectOpen(false)}
        testIdPrefix="single-reject"
      />
      <PromptDialog
        open={bulkRejectOpen}
        title={`reject ${selectedIds.size} row${selectedIds.size === 1 ? "" : "s"}?`}
        message="optional reason (shown to each submitter)"
        placeholder="why reject?"
        confirmLabel="reject all"
        onConfirm={doBulkReject}
        onCancel={() => setBulkRejectOpen(false)}
        testIdPrefix="bulk-reject"
      />
      <ConfirmDialog
        open={bulkDupConfirmOpen}
        title="mark as duplicate of last?"
        message={`${Math.max(selectedIds.size - 1, 0)} row(s) will be merged into the last-selected row.`}
        confirmLabel="yes, merge"
        onConfirm={doBulkMarkDuplicateOf}
        onCancel={() => setBulkDupConfirmOpen(false)}
        testIdPrefix="bulk-dup"
      />
      {/* Msg 234 §6 · TOP STRIP: pending review cards scroll sideways so the
          working area below is wide. Was a 384px left column. */}
      {/* W-B.R7 TOP BAR · 52px · title + counts + on-desk + shortcut hint + + ingest */}
      <div data-testid={QUEUE.list} data-testid-alt="review-top-area" className="flex w-full shrink-0 flex-col" style={{ background: "#FBF4E9" }}>
        <div data-testid="review-topbar" className="flex min-h-[52px] shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-b px-6 py-1" style={{ borderColor: "#E4D7C0" }}>
          {!isDesktop && (
            <button type="button"
                    data-testid="topbar-list-btn"
                    onClick={() => setListDrawerOpen((v) => !v)}
                    className="shrink-0 rounded-full border px-3 py-1 text-[12px] font-bold uppercase tracking-wider"
                    style={{ borderColor: "#1A1A30", color: "#1A1A30", background: "#FFFFFF" }}>
              list
            </button>
          )}
          <h2 className="shrink-0 font-display text-[20px] font-extrabold leading-none" style={{ color: "#1A1A30", fontFamily: "'Poppins', system-ui, sans-serif" }}>review queue</h2>
          <span data-testid="queue-header-count" className="min-w-0 text-[13px]" style={{ color: "#5C5548" }}>
            {(() => {
              if (pendingTotal === null) return "loading…";
              // WW-199.1.b · never show the old list's count while the new filter's list loads.
              if (loadedKey && loadedKey.includes("past_only") !== filterPastEvents) return "loading…";
              const tail = counts
                ? `${counts.to_review} upcoming waiting · ${counts.past_pending} past, still pending`
                : `${waitingTotal ?? pendingTotal} upcoming waiting`;
              if (anyQueueFilter) {
                // WW-199.1.a · in the past view hidden duplicates are named apart so "N match" equals the past count.
                const dupN = filterPastEvents ? filteredPending.filter((p) => p.dedupe_status === "duplicate").length : 0;
                return `${filteredPending.length - dupN} match · ${tail}${dupN ? ` · list includes ${dupN} hidden duplicate${dupN === 1 ? "" : "s"}` : ""}`;
              }
              // WW-199.1 · the list also holds hidden duplicates, so name the list count on its own.
              if (currentIndex >= 0 && filteredPending.length) return `${currentIndex + 1} of ${filteredPending.length} in this list · ${tail}${(() => { const d = filteredPending.filter((p) => p.dedupe_status === "duplicate").length; return d ? ` · list includes ${d} hidden duplicates` : ""; })()}`;
              return tail;
            })()}
          </span>
          {detail && detail.title && (
            <span data-testid="topbar-on-desk" className="truncate text-[13px]" style={{ color: "#5C5548" }}>
              on the desk now: {detail.title}
            </span>
          )}
          {presence.on_desk.length > 1 && (
            <span data-testid="presence-on-desk" className="shrink-0 text-[11px] uppercase tracking-[0.14em]" style={{ color: "#8A8171" }}>
              on the desk now · {presence.on_desk.map((s) => s.first_name).join(", ")}
            </span>
          )}
          <span className="ml-auto hidden shrink-0 text-[11px] lg:inline" style={{ color: "#8A8171" }}>
            j / k move, s save crop, n next that needs a crop
          </span>
          <button
            data-testid={QUEUE.ingestText}
            onClick={() => setShowIngest(true)}
            className="h-9 shrink-0 rounded-full bg-white px-4 text-[12px] font-bold uppercase tracking-wider transition-colors hover:bg-neutral-50"
            style={{ border: "2px solid #1A1A30", color: "#1A1A30" }}
          >+ ingest</button>
          <button
            data-testid="queue-chains-open"
            onClick={() => setChainsOpen(true)}
            className="h-9 shrink-0 rounded-full bg-white px-4 text-[12px] font-bold uppercase tracking-wider transition-colors hover:bg-neutral-50"
            style={{ border: "2px solid #1A1A30", color: "#1A1A30" }}
          >chains</button>
        </div>
        <ChainDesk open={chainsOpen} onClose={() => { setChainsOpen(false); setChainTicked([]); }} ticked={chainTicked}
                   onTickedDone={() => { setChainTicked([]); setSelectedIds(new Set()); }}
                   onOpenEvent={(e) => { if (!e.published) setSelectedId(e.id); else toast.message("published event · open it from Published"); }} />

        <QueueFilterRow hidden={editorOpen} f={{
          loading: listLoading,
          total: pendingTotal, needsCropLabel, posterStateCounts, yearWrongCount, sourceKeys, overlayCount,
          needsCrop: filterNeedsCrop, setNeedsCrop: setFilterNeedsCrop,
          posterNotChecked: filterPosterNotChecked, setPosterNotChecked: setFilterPosterNotChecked,
          yearWrong: filterYearWrong, setYearWrong: setFilterYearWrong,
          pastEvents: filterPastEvents, setPastEvents: setFilterPastEvents,
          overlay: filterOverlay, setOverlay: setFilterOverlay,
          platform: filterPlatform, setPlatform: setFilterPlatform,
          gate: filterGate, setGate: setFilterGate,
          source: filterSource, setSource: setFilterSource,
          capturedBy: filterCapturedBy, setCapturedBy: setFilterCapturedBy,
          dup: filterDup, setDup: setFilterDup,
          iko: filterIko, setIko: setFilterIko,
          sortKey, setSortKey,
        }} />

        {/* W-B.R7 · crop sweep status / counts / errors moved OUT of the
            filter row into a thin line below so the row stays one line. */}
        <div className="flex flex-wrap items-center gap-3 border-b px-6 py-1" style={{ borderColor: "#E4D7C0", background: "#FBF4E9" }}>

            {cropStatus && cropStatus.state && cropStatus.state !== "idle" && (() => {
              // W-B.R24.1 · hide the strip when the sweep is done and
              // the last update is more than 1 hour old.  Prevents a
              // 3-day-old "done · reached 43 of 325" line from staring
              // at Vania forever on live.
              const nowMs = Date.now();
              const updMsEarly = cropStatus.updated_at ? Date.parse(cropStatus.updated_at) : NaN;
              if (cropStatus.state === "done" && !Number.isNaN(updMsEarly)
                  && (nowMs - updMsEarly) > 60 * 60 * 1000) {
                const minsAgo = Math.round((nowMs - updMsEarly) / 60000);
                const label = minsAgo < 60 ? `${minsAgo} min ago`
                  : minsAgo < 60 * 24 ? `${Math.round(minsAgo / 60)} h ago`
                  : `${Math.round(minsAgo / (60 * 24))} days ago`;
                return (
                  <div data-testid="queue-crop-progress-collapsed"
                       className="basis-full text-[11px] text-neutral-500">
                    last sweep · {label}
                  </div>
                );
              }
              // crop button hotfix follow-up (Vania 7 Oct 13:40 UTC) · show
              // "last update Xs ago" and running rate / ETA from the sweep
              // doc so the progress line never looks frozen while a
              // small-batch sweep is making real progress.
              let lastUpdateLabel = "";
              let etaLabel = "";
              let rateLabel = "";
              const touched = cropStatus.rows_touched || 0;
              const total = cropStatus.total_eligible_at_start || 0;
              const updatedMs = cropStatus.updated_at ? Date.parse(cropStatus.updated_at) : NaN;
              const startedMs = cropStatus.started_at ? Date.parse(cropStatus.started_at) : NaN;
              if (!Number.isNaN(updatedMs)) {
                const dSec = Math.max(1, Math.round((nowMs - updatedMs) / 1000));
                lastUpdateLabel = ` · last update ${dSec}s ago`;
              }
              if (!Number.isNaN(startedMs) && touched > 0 && cropStatus.state === "running") {
                const elapsedSec = Math.max(1, (nowMs - startedMs) / 1000);
                const rate = touched / elapsedSec; // rows per second
                if (rate > 0) {
                  const rpm = rate * 60;
                  rateLabel = ` · ${rpm.toFixed(1)}/min`;
                  const remaining = Math.max(total - touched, 0);
                  if (remaining > 0) {
                    const etaSec = remaining / rate;
                    const etaMin = Math.round(etaSec / 60);
                    etaLabel = ` · eta ~${etaMin < 1 ? "<1" : etaMin} min`;
                  }
                }
              }
              const isStale = cropStatus.state === "stale";
              const isRunning = cropStatus.state === "running";
              const isDone = cropStatus.state === "done";
              // W-B.R21 · when stale, read exactly as Vania asked:
              // "Stopped, last ran <date>, reached X of Y" · never
              // "working on poster".
              // W-B.R24.1 · when done, say "done" exactly once (the
              // statusHead has it; drop the trailing " · done" below).
              const statusHead = isStale
                ? `crop sweep · Stopped, last ran ${cropStatus.stale_since || "—"}`
                : `crop sweep · ${cropStatus.state}`;
              return (
                <div
                  data-testid="queue-crop-progress"
                  className="basis-full text-[11px] text-neutral-700"
                >
                  {statusHead}
                  {!isStale && isRunning && typeof cropStatus.rows_touched === "number" ? ` · working on poster ${cropStatus.rows_touched}` : ""}
                  {!isStale && !isRunning && typeof cropStatus.rows_touched === "number" ? ` · reached ${cropStatus.rows_touched}` : ""}
                  {isStale && typeof cropStatus.rows_touched === "number" ? `, reached ${cropStatus.rows_touched}` : ""}
                  {typeof cropStatus.total_eligible_at_start === "number" && cropStatus.total_eligible_at_start > 0
                    ? ` of ${cropStatus.total_eligible_at_start}` : ""}
                  {typeof cropStatus.files_clean_passed === "number" ? ` · clean ${cropStatus.files_clean_passed}` : ""}
                  {typeof cropStatus.files_needs_recrop === "number" ? ` · needs recrop ${cropStatus.files_needs_recrop}` : ""}
                  {typeof cropStatus.files_skipped_existing === "number" && cropStatus.files_skipped_existing > 0
                    ? ` · skipped ${cropStatus.files_skipped_existing}` : ""}
                  {typeof cropStatus.files_vision_failed === "number" && cropStatus.files_vision_failed > 0
                    ? ` · vision failed ${cropStatus.files_vision_failed}` : ""}
                  {typeof cropStatus.files_download_failed === "number" && cropStatus.files_download_failed > 0
                    ? ` · download failed ${cropStatus.files_download_failed}` : ""}
                  {isRunning ? " · running…" : ""}
                  {rateLabel}
                  {etaLabel}
                  {lastUpdateLabel}
                  {cropStatus.started_by ? ` · by ${cropStatus.started_by}` : ""}
                  {(isRunning || isStale) && (
                    <button type="button" data-testid="crop-sweep-stop"
                            onClick={async () => {
                              try {
                                await client.post("/admin/drive-watcher/crop-sweep/stop");
                                // W-B.R21 FU · the backend now returns idle,
                                // but the status poller only ticks while
                                // cropping=true (i.e. running).  When a stale
                                // panel is cleared the poll is dormant, so
                                // we must flip the client state ourselves to
                                // hide the panel immediately.
                                setCropStatus({ state: "idle" });
                                setCropping(false);
                                setCropError("");
                                await refreshCropStatus();
                                toast.success("sweep stopped · state cleared");
                              } catch (e) { toast.error(`stop failed · ${e?.message || ""}`); }
                            }}
                            className="ml-2 whitespace-nowrap rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                            style={{ borderColor: "#9A2222", color: "#9A2222", background: "#FFFFFF" }}>
                      stop + clear
                    </button>
                  )}
                </div>
              );
            })()}
            {cropWaitingCount && !cropping && (
              <div data-testid="queue-crop-count" className="basis-full text-[10px] uppercase tracking-[0.14em] text-neutral-500">
                crop sweep · {cropWaitingCount.total_waiting} posters need a crop
                {cropWaitingCount.parts ? ` · of these: ${cropWaitingCount.parts.to_review} in upcoming waiting · ${cropWaitingCount.parts.past_pending} in past, still pending · ${cropWaitingCount.parts.archived} in archived · ${cropWaitingCount.parts.hidden_duplicates} in hidden duplicates · ${cropWaitingCount.parts.approved_or_rejected} approved or rejected` : ""}
                {counts ? ` · queue: ${counts.to_review} upcoming waiting · ${counts.past_pending} past, still pending · ${counts.archived} archived · ${counts.hidden_duplicates} hidden duplicates` : ""}
                {dupCount && dupCount.includes(" to delete") ? ` · ${dupCount.split(" · ").pop()}` : ""}
              </div>
            )}
            {cropError && (
              <div data-testid="queue-crop-error" className="basis-full text-[11px] text-red-700 whitespace-pre-wrap">
                {cropError}
              </div>
            )}
        </div>
        {selectedIds.size > 0 && (
          <div className="flex flex-wrap items-center gap-1 border-b px-6 py-2" style={{ borderColor: "#E4D7C0", background: "#1A1A30", color: "#FFFFFF" }}>
            <span className="font-semibold text-[12px]">{selectedIds.size} selected</span>
            <button data-testid="bulk-select-all" onClick={selectAllMatching} className="ml-2 rounded px-1.5 py-0.5 text-[11px] hover:bg-white/10">select all filtered</button>
            <button data-testid="bulk-clear" onClick={clearSelection2} className="rounded px-1.5 py-0.5 text-[11px] hover:bg-white/10">clear</button>
            <button data-testid="bulk-approve" onClick={bulkApprove} className="ml-auto rounded bg-emerald-500 px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wider hover:bg-emerald-600">approve ({selectedIds.size})</button>
            <button data-testid="bulk-reject" onClick={bulkReject} className="rounded bg-red-500 px-1.5 py-0.5 text-[11px] hover:bg-red-600">reject</button>
            <button data-testid="bulk-mark-dup" onClick={bulkMarkDuplicateOf} className="rounded bg-amber-500 px-1.5 py-0.5 text-[11px] hover:bg-amber-600">mark as dup of last</button>
            <button data-testid="bulk-add-to-chain" onClick={() => { setChainTicked([...selectedIds]); setChainsOpen(true); }} className="rounded bg-white/20 px-1.5 py-0.5 text-[11px] hover:bg-white/30">add to a chain</button>
          </div>
        )}
        {ingestMatch && (
          <IngestMatchDialog match={ingestMatch} busy={creatingAnyway}
            onUpdate={() => { toast.success("updated the existing event"); setIngestMatch(null); }}
            onCreateAnyway={createAnyway} />
        )}
        {showIngest && (
          <div className="space-y-2 border-b px-6 py-3" style={{ borderColor: "#E4D7C0", background: "#FBF4E9" }}>
            <textarea data-testid={QUEUE.ingestTextInput} placeholder="Paste event text or caption…" value={ingestText} onChange={(e) => setIngestText(e.target.value)} className="h-24 w-full rounded-md border p-2 text-xs" style={{ borderColor: "#E4D7C0" }} />
            <div className="flex gap-2">
              <button data-testid="ingest-text-submit" onClick={doTextIngest} disabled={ingesting} className="rounded-md px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" style={{ background: "#1A1A30" }}>{ingesting ? "…" : "Extract text"}</button>
          <input data-testid={QUEUE.ingestImageInput} ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.heic,.heif,image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={(e) => doImageIngest(e.target.files?.[0])} className="text-xs" />
              <button onClick={() => setShowIngest(false)} className="ml-auto text-xs underline" style={{ color: "#5C5548" }}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* W-B.R7 · THREE COLUMNS under the top area · list 280 · work area flex · details 520 */}
      <div data-testid="review-three-col" className="relative flex min-h-0 min-w-0 flex-1 flex-col lg:flex-row">
        {/* W-B.R8 · LIST COLUMN · desktop inline 280 · tablet slide-over · mobile single-panel */}
        {/* backdrop for the tablet slide-over */}
        {!isDesktop && !isMobile && listDrawerOpen && (
          <div data-testid="review-list-backdrop"
               onClick={() => setListDrawerOpen(false)}
               className="fixed inset-0 z-30 bg-black/35" />
        )}
        <div data-testid="review-list-column"
             className={
               isListInline
                 ? "order-1 flex h-full w-[280px] shrink-0 flex-col overflow-y-auto border-r"
                 : isListDrawer
                   ? (listDrawerOpen
                       ? "fixed inset-y-0 left-[64px] z-40 flex h-full w-[280px] flex-col overflow-y-auto border-r shadow-2xl"
                       : "hidden")
                   : (!selectedId
                       ? "order-1 flex h-full w-full flex-col overflow-y-auto border-b"
                       : "hidden")
             }
             style={{ borderColor: "#E4D7C0", background: "#FBF4E9" }}>
          <div className="flex flex-col gap-2.5 p-3" style={{ scrollbarWidth: "thin" }}>
          {filteredPending.length === 0 && (listLoading || pendingTotal === null) && (
            <p data-testid="queue-list-loading-state" className="p-6 text-center text-sm text-neutral-500 w-full">loading the queue…</p>
          )}
          {filteredPending.length === 0 && !listLoading && pendingTotal !== null && (
            <p className="p-6 text-center text-sm text-neutral-500 w-full">Queue matches empty — asante.</p>
          )}
          {filteredPending.slice(0, visibleCount).map((p) => {
            const checked = selectedIds.has(p.id);
            const active = selectedId === p.id;
            // W-C.48/49 (Vania 9 Oct · live) · resolve the row's drive id
            // for the thumbnail fallback. Server already provides a
            // resolved `screenshot_drive_file_id` (W-C.49 ladder). We
            // keep a client-side fallback ladder that ADDS a rung for
            // rows whose cover_image_url points at the admin blob route
            // (50 live rows with the raw blob stashed as the cover · no
            // poster_classifications, no merged ids).
            const extractDriveId = (url) => {
              if (!url || typeof url !== "string") return "";
              const m = url.match(/\/drive-watcher\/blob\/([^/?#]+)/);
              return m ? m[1] : "";
            };
            const firstDriveForList = p.screenshot_drive_file_id
              || (p.poster_classifications || [])[0]?.drive_file_id
              || (p.merged_drive_file_ids || [])[0]
              || (p.raw_inputs?.[0]?.source_metadata?.drive_file_ids || [])[0]
              || extractDriveId(p.cover_image_url)
              || "";
            const BACKEND = process.env.REACT_APP_BACKEND_URL;
            // W-C.49 · if the cover IS the raw admin blob, treat it as
            // a screenshot (not a cover) so the "needs crop" badge
            // appears and the 160px thumb is requested.
            const coverIsRawBlob = (p.cover_image_url || "")
              .includes("/drive-watcher/blob/");
            // W-C.50 (Vania 9 Oct · live) · outside image link (scout /
            // jsonld rows). cover_image_url is http(s) but there is no
            // screenshot. Routed through the admin image-proxy so hotlink
            // blocks don't break the thumbnail. data-source="link".
            const isOutsideLink = !coverIsRawBlob
              && typeof p.cover_image_url === "string"
              && /^https?:\/\//i.test(p.cover_image_url);
            const coverLikelyBad = coverIsRawBlob
              || !p.cover_image_url
              || !p.cover_source
              || p.cover_source === "needs_recrop"
              || p.cover_source === "skipped";
            let thumbSrc = "";
            let thumbSource = "";
            if (!coverLikelyBad && p.cover_image_url) {
              // WW-198 · own covers at thumbnail size; outside covers through the proxy at 160px.
              thumbSrc = isOutsideLink && !p.cover_image_url.startsWith(BACKEND)
                ? `${BACKEND}/api/admin/drive-watcher/image-proxy?url=${encodeURIComponent(p.cover_image_url)}&w=160`
                : `${coverSrc(p.cover_image_url)}${p.cover_image_url.includes("?") ? "&" : "?"}w=160`;
              thumbSource = "cover";
            } else if (firstDriveForList) {
              thumbSrc = `${BACKEND}/api/admin/drive-watcher/blob/${firstDriveForList}?w=160`;
              thumbSource = "screenshot";
            } else if (isOutsideLink) {
              thumbSrc = `${BACKEND}/api/admin/drive-watcher/image-proxy?url=${encodeURIComponent(p.cover_image_url)}&w=160`;
              thumbSource = "link";
            }
            const posterReallyMissing = !thumbSrc;
            return (
            <div key={p.id}
                 data-testid={QUEUE.item(p.id)}
                 onClick={() => setSelectedId(p.id)}
                 className={`relative flex flex-col w-full gap-1 p-2 rounded-md cursor-pointer transition-colors ${active ? "shadow-sm" : "hover:bg-white"}`} style={{ border: active ? "2px solid #1A1A30" : "1px solid #E4D7C0", background: "#FFFFFF" }}>
              <input
                type="checkbox"
                data-testid={`queue-select-${p.id}`}
                checked={checked}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => toggleSelect(p.id, e.nativeEvent)}
                className="absolute top-1 right-1 z-10"
              />
              <div className="flex gap-2">
                {thumbSrc ? (
                  <div className="relative shrink-0">
                    <img key={thumbSrc}
                         src={thumbsReady ? thumbSrc : undefined} alt=""
                         loading="lazy"
                         decoding="async"
                         data-testid={`queue-card-thumb-${p.id}`}
                         data-source={thumbSource}
                         className="h-[72px] w-14 rounded object-cover"
                         onError={(e) => {
                           // W-C.48/49 · if the cover 404s, flip to the
                           // raw screenshot in place using the resolved
                           // drive id (server-side `screenshot_drive_file_id`
                           // or client-side ladder incl. URL extraction).
                           // Only drop to the amber tile when there is
                           // truly no fallback.
                           const img = e.currentTarget;
                           const alt = firstDriveForList
                             ? `${BACKEND}/api/admin/drive-watcher/blob/${firstDriveForList}?w=160`
                             : "";
                           if (alt && img.src !== alt) {
                             img.src = alt;
                             img.setAttribute("data-source", "screenshot");
                             return;
                           }
                           img.style.display = "none";
                           const ph = img.parentElement.nextElementSibling;
                           if (ph) ph.style.display = "grid";
                         }} />
                    {thumbSource === "screenshot" && (
                      <span data-testid={`queue-card-needs-crop-badge-${p.id}`}
                            className="absolute bottom-0 left-0 right-0 rounded-b bg-amber-500/90 text-center text-[8px] font-semibold uppercase tracking-wider text-white">
                        needs crop
                      </span>
                    )}
                    {thumbSource === "link" && (
                      <span data-testid={`queue-card-link-only-badge-${p.id}`}
                            className="absolute bottom-0 left-0 right-0 rounded-b bg-sky-500/90 text-center text-[8px] font-semibold uppercase tracking-wider text-white">
                        link only
                      </span>
                    )}
                    {Array.isArray(p.overlay_corners) && p.overlay_corners.length > 0 && (
                      <span data-testid={`queue-card-overlay-chip-${p.id}`}
                            title={`overlay in: ${p.overlay_corners.join(", ")}`}
                            className="absolute top-0 right-0 rounded-tr rounded-bl bg-rose-600/90 px-1 text-[8px] font-semibold uppercase tracking-wider text-white">
                        overlay
                      </span>
                    )}
                  </div>
                ) : null}
                <div data-testid={
                        posterReallyMissing ? `queue-card-poster-missing-${p.id}`
                        : (thumbSource === "link" ? `queue-card-link-fail-tile-${p.id}` : undefined)
                     }
                     className="grid h-[72px] w-14 place-items-center rounded bg-amber-50 text-center text-[8px] font-semibold uppercase leading-tight tracking-wider text-amber-800 shrink-0"
                     style={{ display: thumbSrc ? "none" : "grid" }}>
                  {thumbSource === "link"
                    ? <>image<br/>link only<br/>no screenshot</>
                    : <>poster<br/>missing</>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display truncate text-xs font-bold">{p.title || <span className="text-neutral-400">untitled</span>}</p>
                  <div className="mt-1 flex items-center gap-1">
                    <IkoChip iko={p.iko} />
                    {p.is_seed && <span className="rounded bg-amber-100 px-1 text-[9px] font-semibold uppercase tracking-wider text-amber-800">seed</span>}
                  </div>
                  <div className="mt-1"><ConfidenceBar value={p.iko_confidence} /></div>
                </div>
              </div>
              <p className="truncate text-[10px] text-neutral-500">{p.venue_display || "—"} · {p.start_at ? formatEAT(p.start_at) : "no date"}</p>
              <p data-testid={`queue-card-starts-in-${p.id}`} className="text-[10px] font-semibold text-neutral-600">{startsIn(p.start_at)}</p>
              {p.organiser_handle && (
                <a
                  href={`/admin/organisers?edit=${encodeURIComponent((p.organiser_handle || "").replace(/^@+/, ""))}`}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`queue-card-org-${p.id}`}
                  className="block truncate text-[10px] font-semibold text-neutral-700 hover:text-neutral-900 underline decoration-neutral-300 hover:decoration-neutral-900">
                  @{(p.organiser_handle || "").replace(/^@+/, "")}
                </a>
              )}
              <div className="flex items-center gap-1 flex-wrap">
                <SourceBadge source={p.source} captured_by={p.captured_by} />
                {(p.captured_by === "poster_drop" || p.captured_by === "joy") && (
                  <span data-testid={`queue-card-poster-drop-chip-${p.id}`}
                        className="rounded-full px-1.5 py-0.5 text-[9px] font-semibold lowercase tracking-[0.14em]"
                        style={{ background: "var(--iko-polite-tint, #FEF3C7)", color: "#92400E" }}>
                    poster drop · {p.platform || "unknown"}
                  </span>
                )}
                {/* Wave 18b.3 (D61) · supporting iko pills, max 2 shown */}
                {Array.isArray(p.iko_supporting) && p.iko_supporting.slice(0, 2).map((ik) => (
                  <span key={ik} data-testid={`queue-card-iko-supporting-${p.id}-${ik}`}
                        className="rounded-full border border-neutral-300 bg-neutral-50 px-1.5 py-0.5 text-[9px] font-semibold lowercase tracking-[0.14em] text-neutral-700">
                    +{ik}
                  </span>
                ))}
                {/* Wave 18b.5 (D62) · cover_source chip tells staff where the cover came from */}
                {p.cover_source && (
                  <span data-testid={`queue-card-cover-source-${p.id}`}
                        className="rounded-full px-1.5 py-0.5 text-[9px] font-semibold lowercase tracking-[0.14em]"
                        style={{
                          background: p.cover_source === "fixed_crop" ? "#FEE2E2" : "#DBEAFE",
                          color: p.cover_source === "fixed_crop" ? "#991B1B" : "#1E40AF",
                        }}>
                    cover: {p.cover_source.replace(/_/g, " ")}
                  </span>
                )}
                {/* W-B.R21 · 4-state poster chip on every queue card.
                    Visible without opening the row. */}
                {p.poster_gate && (() => {
                  const st = p.poster_gate.state;
                  const palette = st === "confirmed"
                    ? { bg: "#D7F4D9", fg: "#1F7A3A", bd: "#1F7A3A", label: "poster ok" }
                    : st === "no_poster"
                      ? { bg: "#F8DADA", fg: "#7A1A1A", bd: "#9A2222", label: "needs a poster" }
                      : st === "needs_work"
                        ? { bg: "#FCE9CF", fg: "#7A3C09", bd: "#B2550D", label: "poster needs work" }
                        : { bg: "#FCE9CF", fg: "#7A3C09", bd: "#B2550D", label: "poster not checked" };
                  return (
                    <span data-testid={`queue-card-poster-chip-${p.id}`}
                          data-status={st}
                          title={p.poster_gate.label || palette.label}
                          className="rounded-full border px-1.5 py-0.5 text-[9px] font-semibold lowercase tracking-[0.14em]"
                          style={{ background: palette.bg, color: palette.fg, borderColor: palette.bd }}>
                      {palette.label}
                    </span>
                  );
                })()}
                {/* W-B.R26.1 · outside Kenya hold chip + passive
                    "check country" chip (rendered when the text names
                    a non-KE city/country but country is still KE). */}
                {p.country && String(p.country).toUpperCase() !== "KE" && (
                  <span data-testid={`queue-card-country-held-${p.id}`}
                        title={`country · ${p.country}`}
                        className="rounded-full border px-1.5 py-0.5 text-[9px] font-semibold lowercase tracking-[0.14em]"
                        style={{ background: "#FDE7E7", color: "#7A1A1A", borderColor: "#9A2222" }}>
                    outside kenya · held
                  </span>
                )}
                {(!p.country || String(p.country).toUpperCase() === "KE") && p.country_check_hint && (
                  <span data-testid={`queue-card-country-check-${p.id}`}
                        title={`text mentions ${p.country_check_hint} · open to set country`}
                        className="rounded-full border px-1.5 py-0.5 text-[9px] font-semibold lowercase tracking-[0.14em]"
                        style={{ background: "#FCE9CF", color: "#7A3C09", borderColor: "#B2550D" }}>
                    check country · {p.country_check_hint}
                  </span>
                )}
                {p.dedupe_status === "duplicate" && (
                  <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-amber-700">⚠ dup</span>
                )}
                {p.dedupe_status === "possible duplicate" && (
                  <span data-testid={`queue-card-near-dup-${p.id}`}
                        className="rounded-full px-1.5 py-0.5 text-[9px] font-semibold lowercase tracking-[0.14em]"
                        style={{ background: "#FEF3C7", color: "#92400E" }}>
                    ⚠ possible dup{p.near_dup_label ? ` · ${p.near_dup_label}` : ""}
                  </span>
                )}
                {(p.gates?.price_missing || (p.price || "").toLowerCase() === "price to confirm") && (
                  <span data-testid={`price-missing-${p.id}`}
                        className="text-[9px] font-semibold uppercase tracking-[0.14em] text-amber-700">
                    ⚠ price missing
                  </span>
                )}
                {p.link_status && p.link_status !== "ok" && (
                  <span data-testid={`link-broken-${p.id}`}
                        className="text-[9px] font-semibold uppercase tracking-[0.14em]" style={{ color: "#B32020" }}>
                    ⚠ link {p.link_status.replace(/_/g, " ")}
                  </span>
                )}
                {(p.blocking_notes || []).length > 0 && (
                  <span data-testid={`blocking-notes-${p.id}`}
                        className="text-[9px] font-semibold uppercase tracking-[0.14em] text-rose-700">
                    ⚠ {(p.blocking_notes || [])[0]}
                  </span>
                )}
              </div>
              {p.ticket_source && (
                <p className="text-[9px] uppercase tracking-[0.14em] text-neutral-500"
                   data-testid={`ticket-source-${p.id}`}>
                  via {p.ticket_source}
                </p>
              )}
              {/* Msg 238 · another admin is currently viewing this row. */}
              {presence.locks[p.id] && presence.locks[p.id].user_id !== user?.user_id && (
                <span data-testid={`lock-marker-${p.id}`}
                      className="mt-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-amber-800">
                  {presence.locks[p.id].first_name} is on this
                </span>
              )}
            </div>
            );
          })}
          {filteredPending.length > visibleCount && (
            <button type="button"
                    data-testid="review-queue-show-more"
                    onClick={() => setVisibleCount((n) => n + 30)}
                    className="w-full rounded-md border border-dashed px-4 py-2 text-xs font-semibold hover:border-neutral-500" style={{ borderColor: "#E4D7C0", color: "#5C5548" }}>
              show {Math.min(30, filteredPending.length - visibleCount)} more · {filteredPending.length - visibleCount} left
            </button>
          )}
          </div>
        </div>

      {/* W-B.R8 · right-of-list · middle work area + 520 details column.
          <xl stacks the work area above the details column so there's no
          88px-wide work area at 1024.  <md single-panel. */}
      {detail ? (
        <div data-testid={QUEUE.reviewer} className="order-2 flex min-h-0 min-w-0 flex-1 flex-col lg:flex-row">
          {isMobile && (
            <button type="button"
                    data-testid="detail-back-to-list"
                    onClick={() => { clearSelection(); setListDrawerOpen(false); }}
                    className="shrink-0 whitespace-nowrap border-b px-4 py-2 text-[12px] font-bold uppercase tracking-wider"
                    style={{ borderColor: "#E4D7C0", background: "#FFFFFF", color: "#1A1A30" }}>
              ← back to list
            </button>
          )}
          {/* Image / raw side.
              W-C.47 (Vania 9 Oct · live priority) · when the cover URL
              is empty, 404s, or the row's cover_source flags it needs
              a crop AND a drive id is linked, render the manual crop
              editor directly as the preview. The crop box ON the raw
              screenshot IS the preview · no dashed "poster missing"
              tile stacked above it, so staff can see the poster and
              drag the box in one place. */}
          <div data-testid="review-work-area" data-editor-open={editorOpen ? "1" : "0"} data-patch-active={editorOpen && editorTab === "patch" ? "1" : "0"} className={`flex min-h-0 min-w-0 flex-1 flex-col ${editorOpen ? "overflow-hidden lg:border-r-0" : "overflow-hidden border-b lg:border-b-0 lg:border-r lg:min-w-[320px]"} lg:h-full`} style={{ borderColor: "#E4D7C0", background: "#FBF4E9", padding: editorOpen ? 0 : (isMobile ? "12px 16px" : "20px 28px"), minHeight: isMobile ? 360 : undefined }}>
            {/* W-B.R16 · one editor, two tabs · takes over the whole
                right side when open · otherwise shows the preview card
                plus a single "edit poster" button. */}
            {editorOpen ? (() => {
              const firstDrive = (detail.raw_inputs?.[0]?.source_metadata?.drive_file_ids || [])[0]
                || (detail.merged_drive_file_ids || [])[0]
                || (detail.poster_classifications || [])[0]?.drive_file_id
                || "";
              const defaultEditorTab = (() => {
                const pg = detail.poster_gate?.state;
                if (pg === "no_poster" || !firstDrive || coverErrored) return "replace";
                return editorTab;
              })();
              const availableDriveFiles = Array.from(new Set([
                ...(detail.poster_classifications || []).map((c) => c.drive_file_id).filter(Boolean),
                ...(detail.merged_drive_file_ids || []).filter(Boolean),
                ...(detail.raw_inputs || []).flatMap((r) => r?.source_metadata?.drive_file_ids || []).filter(Boolean),
              ]));
              return (
                <PosterEditor
                  key={`editor-${detail.id}`}
                  coverMissing={!!detail.cover_blob_missing}
                  pendingId={detail.id}
                  driveFileId={firstDrive}
                  initialBbox={detail.cover_bbox}
                  coverUrl={detail.cover_image_url}
                  cropBaseCoverUrl={detail.crop_base_cover_url || ""}
                  originalCoverUrl={detail.original_cover_image_url}
                  defaultTab={defaultEditorTab}
                  posterConfirmed={!!detail.poster_confirmed}
                  posterGate={detail.poster_gate}
                  posterCheck={detail.poster_check}
                  coverFingerprint={detail.cover_image_url || ""}
                  availableDriveFiles={availableDriveFiles}
                  onReplaceUploaded={async (file) => {
                    try {
                      const fd = new FormData();
                      fd.append("file", file);
                      const r = await client.post(`/admin/drive-watcher/upload-cover/${encodeURIComponent(detail.id)}`, fd, { timeout: 60000, headers: { "Content-Type": "multipart/form-data" } });
                      if (r.data?.ok) {
                        const patch = {
                          cover_image_url: r.data.cover_image_url,
                          crop_base_cover_url: r.data.crop_base_cover_url,
                          cover_source: "manual_upload",
                          cover_bbox: { left: 0, top: 0, right: 1, bottom: 1 },
                          poster_check: null,
                          poster_confirmed: false,
                          poster_status: r.data.poster_status,
                          poster_gate: r.data.poster_gate,
                        };
                        setDetail((d) => ({ ...(d || detail), ...patch }));
                        setDraft((d) => ({ ...d, cover_image_url: r.data.cover_image_url }));
                        setCoverErrored(false);
                        setPending((rows) => rows.map((p) => p.id === detail.id ? { ...p, ...patch } : p));
                        toast.success("uploaded · confirm the new poster now");
                      } else {
                        toast.error(`upload failed · ${r.data?.error || ""}`);
                      }
                    } catch (e) {
                      toast.error(`upload failed · ${e?.message || ""}`);
                    }
                  }}
                  onReplaceDrivePicked={async (did) => {
                    try {
                      await client.post(`/admin/drive-watcher/manual-crop/${encodeURIComponent(detail.id)}`, { drive_file_id: did, bbox: { left: 0, top: 0, right: 1, bottom: 1 } });
                      toast.success("switched screenshot · crop + confirm now");
                      // reload detail
                      const r = await client.get(`/admin/queue/${encodeURIComponent(detail.id)}`);
                      setDetail(r.data.pending);
                    } catch (e) { toast.error(`switch failed · ${e?.message || ""}`); }
                  }}
                  onPosterConfirmed={async () => {
                    try {
                      const r = await client.post(`/admin/queue/${encodeURIComponent(detail.id)}/poster-confirm`);
                      // R18 FAIL 1 · patch poster_gate + poster_check in
                      // local state immediately so the chip + button flip
                      // within the same tick (no refetch, editor stays
                      // mounted, no unsaved boxes lost).
                      setDetail((d) => ({ ...(d || detail),
                        poster_check: r.data.poster_check,
                        poster_confirmed: true,
                        poster_status: "ok",
                        poster_gate: r.data.poster_gate }));
                      setPending((rows) => rows.map((p) => p.id === detail.id ? { ...p, poster_confirmed: true, poster_status: "ok", poster_gate: r.data.poster_gate } : p));
                      toast.success("poster confirmed");
                    } catch (err) {
                      toast.error(`confirm failed · ${err?.response?.data?.detail?.message || err?.message || ""}`);
                    }
                  }}
                  onPosterUnconfirmed={async () => {
                    try {
                      const r = await client.post(`/admin/queue/${encodeURIComponent(detail.id)}/poster-unconfirm`);
                      setDetail((d) => ({ ...(d || detail),
                        poster_confirmed: false,
                        poster_status: r.data.poster_status,
                        poster_gate: r.data.poster_gate,
                        poster_check: { confirmed: false, confirmed_cover_url: "" } }));
                      setPending((rows) => rows.map((p) => p.id === detail.id ? { ...p, poster_confirmed: false, poster_status: r.data.poster_status, poster_gate: r.data.poster_gate } : p));
                      toast.success("undone");
                    } catch (err) {
                      toast.error(`undo failed · ${err?.message || ""}`);
                    }
                  }}
                  onClose={() => setEditorOpen(false)}
                  onCropSaved={(resp) => {
                    const nowStr = new Date().toLocaleTimeString([], {
                      hour: "2-digit", minute: "2-digit", second: "2-digit",
                    });
                    // R18 FAIL 1 · chip flips to not_checked immediately.
                    const not_checked_gate = { state: "not_checked", label: "poster not checked", reasons: ["human has not confirmed this cover yet"], passes: false };
                    setDetail((d) => ({ ...(d || detail),
                      cover_image_url: resp.cover_image_url,
                      crop_base_cover_url: resp.crop_base_cover_url || "",
                      cover_source: resp.cover_source || "manual_crop",
                      cover_bbox: resp.cover_bbox,
                      manual_crop_at: resp.manual_crop_at,
                      poster_confirmed: false,
                      poster_status: "not_checked",
                      poster_gate: not_checked_gate,
                    }));
                    setDraft((d) => ({ ...d, cover_image_url: resp.cover_image_url }));
                    setCoverErrored(false);
                    setPending((rows) => rows.map((r) => r.id === detail.id ? {
                      ...r,
                      cover_image_url: resp.cover_image_url,
                      cover_source: resp.cover_source || "manual_crop",
                      poster_confirmed: false,
                      poster_status: "not_checked",
                      poster_gate: not_checked_gate,
                    } : r));
                    setSavedFlash({ pendingId: detail.id, at: nowStr });
                    setTimeout(() => setSavedFlash((f) => (
                      f && f.pendingId === detail.id ? null : f)), 10000);
                  }}
                  onPatchApplied={(resp) => {
                    const not_checked_gate = { state: "not_checked", label: "poster not checked", reasons: ["human has not confirmed this cover yet"], passes: false };
                    setDetail((d) => ({ ...(d || detail),
                      cover_image_url: resp.cover_image_url,
                      cover_source: resp.cover_source || "manual_patch",
                      patched_at: resp.patched_at,
                      original_cover_image_url: resp.original_cover_image_url || (d?.original_cover_image_url || detail.cover_image_url || ""),
                      poster_confirmed: false,
                      poster_status: "not_checked",
                      poster_gate: not_checked_gate,
                    }));
                    setDraft((d) => ({ ...d, cover_image_url: resp.cover_image_url }));
                    setPending((rows) => rows.map((r) => r.id === detail.id ? {
                      ...r, cover_image_url: resp.cover_image_url, cover_source: resp.cover_source || "manual_patch",
                      poster_confirmed: false, poster_status: "not_checked", poster_gate: not_checked_gate,
                    } : r));
                  }}
                  onPatchRestored={(resp) => {
                    const url = resp?.cover_image_url || detail.original_cover_image_url;
                    if (!url) return;
                    const not_checked_gate = { state: "not_checked", label: "poster not checked", reasons: ["human has not confirmed this cover yet"], passes: false };
                    setDetail((d) => ({ ...(d || detail), cover_image_url: url, cover_source: "manual_crop",
                      poster_confirmed: false, poster_status: "not_checked", poster_gate: not_checked_gate }));
                    setDraft((d) => ({ ...d, cover_image_url: url }));
                    setPending((rows) => rows.map((r) => r.id === detail.id ? { ...r, cover_image_url: url,
                      poster_confirmed: false, poster_status: "not_checked", poster_gate: not_checked_gate } : r));
                  }}
                  onNoPoster={async () => {
                    try {
                      await client.post(`/admin/queue/${encodeURIComponent(detail.id)}/mark-no-poster`);
                    } catch { /* best-effort */ }
                    setPending((rows) => rows.map((r) => r.id === detail.id ? { ...r, cover_source: "skipped" } : r));
                    setEditorOpen(false);
                    toast.success("marked no poster · moving on");
                    setTimeout(() => goNextNeedsCrop(), 200);
                  }}
                />
              );
            })() : (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {(() => {
              const firstDrive = (detail.raw_inputs?.[0]?.source_metadata?.drive_file_ids || [])[0]
                || (detail.merged_drive_file_ids || [])[0]
                || (detail.poster_classifications || [])[0]?.drive_file_id
                || "";
              const coverSource = (detail.cover_source || "").toLowerCase();
              const coverKnownBad = !detail.cover_image_url
                || detail.cover_blob_missing
                || coverSource === "needs_recrop"
                || coverSource === "skipped"
                || coverErrored;
              const nextTab = coverKnownBad ? "crop" : "patch";
              const openEditor = () => { setEditorTab(coverErrored || detail.cover_blob_missing || !firstDrive ? "replace" : nextTab); setEditorOpen(true); };
              const rawUrl = firstDrive
                ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/drive-watcher/blob/${firstDrive}`
                : "";
              // WW-150 (Vania 13 Oct) · the poster chip + confirm /
              // needs-work buttons MUST render for every event, including
              // link-only rows that have no linked Drive screenshot.
              // Vania must be able to confirm (or flag) a poster from the
              // details panel without opening the editor. "Edit poster"
              // stays firstDrive-gated because the editor needs a Drive
              // image to crop · for link-only rows the inline buttons
              // below carry the whole poster flow.
              const posterLooksGood = async () => {
                // WW-150/151 fix 4 (Vania 13 Oct · D-2 QA FAIL 2) ·
                // never fire against a stale card. selectedIdRef is
                // updated the moment Vania clicks a different card, so
                // if these two no longer match the user clicked a new
                // card during the detail load.
                if (!detail || detail.id !== selectedIdRef.current || detailLoading) {
                  toast.message("hold on · loading the selected event…");
                  return;
                }
                const actOn = detail.id;
                try {
                  const r = await client.post(`/admin/queue/${encodeURIComponent(actOn)}/poster-confirm`);
                  if (actOn !== selectedIdRef.current) return;
                  setDetail((d) => ({ ...(d || detail),
                    poster_check: r.data.poster_check,
                    poster_confirmed: true,
                    poster_status: "ok",
                    poster_gate: r.data.poster_gate,
                    // FAIL 3 · pick up the fresh gates object so the
                    // footer flips from "cannot publish: working on
                    // it…" to "ready to publish" without a reselect.
                    gates: r.data.gates,
                    gates_pass: r.data.gates_pass,
                  }));
                  setPending((rows) => rows.map((p) => p.id === actOn ? { ...p, poster_confirmed: true, poster_status: "ok", poster_gate: r.data.poster_gate } : p));
                  toast.success("poster confirmed");
                } catch (err) {
                  toast.error(`confirm failed · ${err?.response?.data?.detail?.message || err?.message || ""}`);
                }
              };
              const posterNeedsWork = async () => {
                if (!detail || detail.id !== selectedIdRef.current || detailLoading) {
                  toast.message("hold on · loading the selected event…");
                  return;
                }
                const actOn = detail.id;
                try {
                  const r = await client.post(`/admin/queue/${encodeURIComponent(actOn)}/poster-needs-work`);
                  if (actOn !== selectedIdRef.current) return;
                  setDetail((d) => ({ ...(d || detail),
                    poster_check: { confirmed: false, confirmed_cover_url: "" },
                    poster_confirmed: false,
                    poster_status: r.data.poster_status,
                    poster_gate: r.data.poster_gate,
                    cover_source: "needs_work",
                    gates: r.data.gates,
                    gates_pass: r.data.gates_pass,
                  }));
                  setPending((rows) => rows.map((p) => p.id === actOn ? { ...p, poster_confirmed: false, poster_status: r.data.poster_status, poster_gate: r.data.poster_gate, cover_source: "needs_work" } : p));
                  toast.success("flagged · poster needs work");
                } catch (err) {
                  toast.error(`flag failed · ${err?.response?.data?.detail?.message || err?.message || ""}`);
                }
              };
              const posterUndoConfirm = async () => {
                if (!detail || detail.id !== selectedIdRef.current || detailLoading) {
                  toast.message("hold on · loading the selected event…");
                  return;
                }
                const actOn = detail.id;
                try {
                  const r = await client.post(`/admin/queue/${encodeURIComponent(actOn)}/poster-unconfirm`);
                  if (actOn !== selectedIdRef.current) return;
                  setDetail((d) => ({ ...(d || detail),
                    poster_confirmed: false,
                    poster_status: r.data.poster_status,
                    poster_gate: r.data.poster_gate,
                    poster_check: { confirmed: false, confirmed_cover_url: "" },
                    gates: r.data.gates,
                    gates_pass: r.data.gates_pass,
                  }));
                  setPending((rows) => rows.map((p) => p.id === actOn ? { ...p, poster_confirmed: false, poster_status: r.data.poster_status, poster_gate: r.data.poster_gate } : p));
                  toast.success("undone");
                } catch (err) {
                  toast.error(`undo failed · ${err?.message || ""}`);
                }
              };
              // WW-150/151 fix 4 · while the server's reply for the
              // currently-selected card has not landed yet, the
              // on-screen `detail` object still carries the previous
              // row's id. Flip every action to disabled so a quick
              // click can never target a stale row.
              const detailMatchesSelection = detail && detail.id === selectedIdRef.current && !detailLoading;
              // WW-178 · status reads as a label above the buttons, not a third button.
              const PosterButtons = (
                <>
                {detail.poster_gate && (
                  <p data-testid="poster-status-chip"
                     data-status={detail.poster_gate.state}
                     className="mx-auto mt-3 text-center text-[12px] lowercase"
                     style={{ color: detail.poster_gate.state === "confirmed" ? "#1F7A3A" : detail.poster_gate.state === "no_poster" ? "#7A1A1A" : "#7A3C09" }}>
                    poster: {(detail.poster_gate.label || "status unknown").toLowerCase().replace(/^poster\s*/, "")}
                  </p>
                )}
                <div data-testid="poster-buttons-details" className="mx-auto mt-2 flex w-full shrink-0 flex-col items-center justify-center gap-2" style={{ maxWidth: 512 }}>
                  {detail.poster_gate && (
                    <div className="flex w-full flex-wrap items-center justify-center gap-2">
                      {detail.poster_gate.state === "confirmed" ? (
                        <button type="button" data-testid="poster-undo-confirm-details"
                                disabled={!detailMatchesSelection}
                                onClick={posterUndoConfirm}
                                title={detailMatchesSelection ? "undo confirm" : "loading selected event…"}
                                className="inline-flex items-center justify-center whitespace-nowrap rounded-md border px-3 py-1.5 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                                style={{ borderColor: "#1A1A30", background: "#FFFFFF", color: "#1A1A30" }}>
                          undo confirm
                        </button>
                      ) : (() => {
                        // WW-150.2 (Vania 13 Oct) · link-only rows must
                        // show BOTH buttons. "Poster looks good" is
                        // disabled only when there is literally nothing
                        // to look at (no cover URL at all) or the image
                        // failed to load from the public internet · in
                        // that case the reason sits next to the button
                        // so Vania knows why it is grey. "Poster needs
                        // work" is ALWAYS usable so a broken link can
                        // still be flagged out of the publish gate.
                        const hasCoverUrl = !!(detail.cover_image_url || "").trim();
                        const canSee = hasCoverUrl && !coverErrored && !detail.cover_blob_missing;
                        const disabledReason = !hasCoverUrl
                          ? "no cover link"
                          : detail.cover_blob_missing
                            ? "poster missing"
                            : coverErrored
                              ? "can't see the image"
                              : "";
                        return (
                          <>
                            <button type="button" data-testid="poster-confirm-details"
                                    disabled={!canSee || !detailMatchesSelection || draftCoverFailed}
                                    onClick={posterLooksGood}
                                    title={!detailMatchesSelection ? "loading selected event…" : draftCoverFailed ? "the cover link did not load" : (disabledReason || "mark this poster as good")}
                                    className="inline-flex items-center justify-center whitespace-nowrap rounded-md border-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                                    style={{ borderColor: "#1A1A30", background: "#FFD60A", color: "#1A1A30" }}>
                              poster looks good
                            </button>
                            <button type="button" data-testid="poster-needs-work-details"
                                    disabled={!detailMatchesSelection}
                                    onClick={posterNeedsWork}
                                    title={!detailMatchesSelection ? "loading selected event…" : "flag · poster needs work"}
                                    className="inline-flex items-center justify-center whitespace-nowrap rounded-md border px-3 py-1.5 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                                    style={{ borderColor: "#B2550D", background: "#FFFFFF", color: "#7A3C09" }}>
                              poster needs work
                            </button>
                            {(draftCoverFailed || disabledReason) && (
                              <span data-testid="poster-looks-good-disabled-reason"
                                    className="w-full text-center text-[11px]"
                                    style={{ color: "#7A3C09" }}>
                                "poster looks good" is off · {draftCoverFailed ? "the cover link did not load" : disabledReason}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                  {(
                    <button type="button" data-testid="edit-poster-button" onClick={openEditor}
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border-2 px-5 py-3 text-sm font-bold uppercase tracking-wider"
                            style={{ borderColor: "#1A1A30", background: "#FFD60A", color: "#1A1A30" }}>
                      <span className="whitespace-nowrap">edit poster</span>
                      <span aria-hidden className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-sm px-1 text-[10px] font-bold" style={{ background: "#1A1A30", color: "#FFD60A", lineHeight: 1 }}>e</span>
                    </button>
                  )}
                </div>
                </>
              );
              if (!coverKnownBad) {
                return (
                  <>
                    <div data-testid="detail-screenshot-preview" data-preview-state="cover" className="mx-auto flex w-full min-h-0 flex-1 flex-col overflow-hidden rounded-xl border bg-white shadow-sm" style={{ width: "100%", maxWidth: isMobile ? "100%" : 512, maxHeight: isMobile ? 360 : "100%", borderColor: "#E4D7C0" }}>
                      <img key={detail.cover_image_url}
                           src={coverSrc(detail.cover_image_url)} alt="poster"
                           loading="eager"
                           decoding="async"
                           data-testid="detail-cover-img"
                           className="mx-auto block max-h-full max-w-full flex-1 rounded-lg object-contain"
                           onError={() => setCoverErrored(true)} />
                    </div>
                    {PosterButtons}
                  </>
                );
              }
              if (firstDrive && !(detail.cover_blob_missing && rawState !== "ok")) {
                return (
                  <>
                    <div data-testid="detail-screenshot-preview" data-preview-state="screenshot" className="mx-auto flex w-full min-h-0 flex-1 flex-col items-center justify-center overflow-hidden rounded-xl border bg-white p-2 shadow-sm" style={{ width: "100%", maxWidth: isMobile ? "100%" : 512, maxHeight: isMobile ? 360 : "100%", borderColor: "#E4D7C0" }}>
                      <img src={rawUrl} alt="raw screenshot"
                           data-testid="detail-screenshot-img"
                           onLoad={(e) => setRawState(e.currentTarget.naturalWidth > 0 ? "ok" : "failed")}
                           onError={() => setRawState("failed")}
                           loading="eager"
                           decoding="async"
                           className="mx-auto block max-h-full max-w-full flex-1 rounded-md object-contain" />
                      <p className="mt-2 text-center text-[11px]" style={{ color: detail.cover_blob_missing ? "#7A3C09" : "#8A8171" }}
                         data-testid={detail.cover_blob_missing ? "detail-poster-missing" : undefined}>
                        {detail.cover_blob_missing
                          ? (rawState === "ok" ? "poster missing · this is the raw screenshot · replace it with edit poster below" : "poster missing · replace it with edit poster below")
                          : "this poster needs a crop · tap edit poster below"}
                      </p>
                    </div>
                    {PosterButtons}
                  </>
                );
              }
              // WW-150.2 (Vania 13 Oct) · link-only rows whose cover
              // URL errors out used to fall straight through to the
              // "no screenshot linked" tile with no poster buttons ·
              // Vania could never clear "poster not checked" on them.
              // Fix: if a cover link exists but the image fails to
              // load, render a dedicated broken-link tile + the
              // PosterButtons block (looks-good disabled with reason,
              // needs-work always usable).
              if ((detail.cover_image_url || "").trim()) {
                return (
                  <>
                    <div data-testid="detail-screenshot-broken"
                         className="mx-auto flex w-full min-h-0 flex-1 flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-amber-400 bg-amber-50 p-6 text-center"
                         style={{ width: "100%", maxWidth: isMobile ? "100%" : 512, maxHeight: isMobile ? 360 : "100%" }}>
                      {/* WW-147.1 · load the raw screenshot out of sight; swap it in only once it really rendered. */}
                      {detail.cover_blob_missing && firstDrive && rawState === "loading" && (
                        <img src={rawUrl} alt="" aria-hidden style={{ display: "none" }}
                             onLoad={(e) => setRawState(e.currentTarget.naturalWidth > 0 ? "ok" : "failed")}
                             onError={() => setRawState("failed")} />
                      )}
                      <p className="text-sm font-semibold text-amber-800" data-testid={detail.cover_blob_missing ? "detail-poster-missing" : undefined}>
                        {detail.cover_blob_missing ? "poster missing · replace it" : "image can't be loaded"}
                      </p>
                      <p className="break-all text-[11px] text-amber-800/80">
                        {detail.cover_image_url}
                      </p>
                      <p className="text-[11px] text-amber-800/80">
                        the cover link exists but preview can't reach the image · you can still mark it "poster needs work" so it leaves the publish queue
                      </p>
                    </div>
                    {PosterButtons}
                  </>
                );
              }
              return (
                <div data-testid="detail-screenshot-missing" className="mx-auto flex w-full min-h-0 flex-1 flex-col items-center gap-3 rounded-xl border-2 border-dashed border-amber-400 bg-amber-50 p-6 text-center" style={{ width: "100%", maxWidth: isMobile ? "100%" : 512, maxHeight: isMobile ? 360 : "100%" }}>
                  <p className="text-sm font-semibold text-amber-800">no screenshot linked</p>
                  <p className="text-[11px] text-amber-800/80">
                    paste a public cover image URL in the cover field below · or re-ingest with a screenshot
                  </p>
                  <label className="label-tiny w-full text-left">cover image url</label>
                  <input type="url" placeholder="https://…"
                         data-testid="detail-screenshot-missing-url"
                         value={draft.cover_image_url || ""}
                         onChange={(e) => setDraft((d) => ({
                           ...d, cover_image_url: e.target.value,
                           original_cover_image_url: d.original_cover_image_url || detail.cover_image_url || "",
                         }))}
                         className="w-full rounded-md border border-neutral-300 p-2 text-sm" />
                </div>
              );
            })()}
            </div>
            )}
            {/* W-B.R16 · the inline PatchToolPanel + ManualCropEditor
                entry points moved into PosterEditor above.  No second
                path from this surface. */}
          </div>

          {/* Form side. Msg 234 URGENT · flex column with the fields scrolling
              (flex-1 min-h-0 overflow-y-auto) and the approve bar sticky at
              the bottom so it's always in reach. */}
          {/* R15 · patch focus mode takes over the right side ·
              review-details stays mounted (form state preserved) but
              display:none keeps it out of the layout.  Comes back the
              moment Vania taps 'done'. */}
          <div data-testid="review-details" data-patch-hidden={editorOpen ? "1" : "0"} data-editor-hidden={editorOpen ? "1" : "0"} className="flex min-h-0 shrink-0 flex-col border-t lg:h-full lg:border-t-0 lg:border-l lg:min-w-[480px] lg:basis-[55%] xl:min-w-[560px] xl:basis-[52%] lg:max-w-[720px]" style={{ borderColor: "#E4D7C0", background: "#FFFFFF", display: editorOpen ? "none" : undefined }}>
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto" style={{ padding: "18px 24px 16px" }}>
            <div className="mb-6 flex items-center gap-2" data-testid="group-provenance">
              <SourceBadge source={detail.source} captured_by={detail.captured_by} />
              <IkoChip iko={draft.iko} />
              <span className="ml-auto text-xs text-neutral-500">confidence {(detail.iko_confidence * 100).toFixed(0)}%</span>
            </div>

            {/* W-B.R4 · group · event. */}
            <p className="label-tiny mt-2" data-testid="group-event">event</p>
            <label className="label-tiny mt-2">Title</label>
            <input data-testid={QUEUE.fieldTitle} className="mt-1 w-full rounded-md border border-neutral-300 p-2 text-lg font-bold"
              value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="label-tiny">Start (Nairobi time)</label>
                <input data-testid={QUEUE.fieldStartAt} type="datetime-local" className="mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm"
                  value={(draft.start_at || "").slice(0, 16)} onChange={(e) => setDraft({ ...draft, start_at: e.target.value })} />
              </div>
              <div>
                <label className="label-tiny">End (optional)</label>
                <input data-testid={QUEUE.fieldEndAt} type="datetime-local" className="mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm"
                  value={(draft.end_at || "").slice(0, 16)} onChange={(e) => setDraft({ ...draft, end_at: e.target.value })} />
              </div>
            </div>

            {/* Kagz Msg 236 #3 + Wave 3 #7 (Feb 2026) · one event, several
                slots (film runs, plays over a weekend, a class twice a day).
                Members see the next upcoming slot on the card; the event page
                lists every slot with its own time and (optionally) its own
                price and ticket link. */}
            <div className="mt-3" data-testid="slots-block">
              <label className="label-tiny">extra slots (screenings · multi-day runs)</label>
              {(draft.slots || []).map((s, idx) => (
                <div key={s.id || idx} className="mt-2 grid gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-2"
                     data-testid={`slot-row-${idx}`}
                     style={{ gridTemplateColumns: "1fr 1fr" }}>
                  <div className="col-span-2 flex items-center gap-2">
                    <input type="text"
                           placeholder="label (e.g. matinee, day 2 — optional)"
                           data-testid={`slot-label-${idx}`}
                           className="flex-1 rounded-md border border-neutral-300 p-1.5 text-xs"
                           value={s.label || ""}
                           onChange={(e) => {
                             const next = [...draft.slots];
                             next[idx] = { ...next[idx], label: e.target.value };
                             setDraft({ ...draft, slots: next });
                           }} />
                    <button type="button" data-testid={`slot-remove-${idx}`}
                            onClick={() => setDraft({ ...draft, slots: draft.slots.filter((_, i) => i !== idx) })}
                            className="rounded-full border border-neutral-300 px-2 py-1 text-[11px] text-neutral-600 hover:bg-white">
                      remove slot
                    </button>
                  </div>
                  <div>
                    <label className="label-tiny">start</label>
                    <input type="datetime-local"
                           data-testid={`slot-start-${idx}`}
                           className="mt-1 w-full rounded-md border border-neutral-300 p-1.5 text-xs"
                           value={(s.start_at || "").slice(0, 16)}
                           onChange={(e) => {
                             const next = [...draft.slots];
                             next[idx] = { ...next[idx], start_at: e.target.value };
                             setDraft({ ...draft, slots: next });
                           }} />
                  </div>
                  <div>
                    <label className="label-tiny">end (optional)</label>
                    <input type="datetime-local"
                           data-testid={`slot-end-${idx}`}
                           className="mt-1 w-full rounded-md border border-neutral-300 p-1.5 text-xs"
                           value={(s.end_at || "").slice(0, 16)}
                           onChange={(e) => {
                             const next = [...draft.slots];
                             next[idx] = { ...next[idx], end_at: e.target.value };
                             setDraft({ ...draft, slots: next });
                           }} />
                  </div>
                  <div>
                    <label className="label-tiny">price (optional · falls back to event price)</label>
                    <input type="text"
                           data-testid={`slot-price-${idx}`}
                           placeholder='"KES 1,500" · "free" · blank'
                           className="mt-1 w-full rounded-md border border-neutral-300 p-1.5 text-xs"
                           value={s.price || ""}
                           onChange={(e) => {
                             const next = [...draft.slots];
                             next[idx] = { ...next[idx], price: e.target.value };
                             setDraft({ ...draft, slots: next });
                           }} />
                  </div>
                  <div>
                    <label className="label-tiny">ticket link (optional)</label>
                    <input type="url" placeholder="https://…"
                           data-testid={`slot-link-${idx}`}
                           className="mt-1 w-full rounded-md border border-neutral-300 p-1.5 text-xs"
                           value={s.ticket_link || ""}
                           onChange={(e) => {
                             const next = [...draft.slots];
                             next[idx] = { ...next[idx], ticket_link: e.target.value };
                             setDraft({ ...draft, slots: next });
                           }} />
                  </div>
                </div>
              ))}
              <button type="button" data-testid="slot-add"
                      onClick={() => setDraft({ ...draft, slots: [...(draft.slots || []), { id: crypto.randomUUID(), start_at: "", end_at: "", price: "", ticket_link: "", label: "" }] })}
                      className="mt-2 text-xs font-semibold text-neutral-700 hover:underline">
                + add another slot (per-slot time · optional price · optional link)
              </button>
            </div>

            <div className="mt-4">
              <label className="label-tiny">Venue (pick or create)</label>
              <div className="mt-1">
                <VenueCombobox value={draft.venue_id || ""}
                               onChange={(id, rec) => {
                                 setDraft({ ...draft, venue_id: id || null,
                                            venue_display: rec ? rec.label : draft.venue_display,
                                            // WW-146.k · country follows the venue picked (still editable)
                                            ...(rec?.country ? { country: String(rec.country).toUpperCase() } : {}) });
                                 // WW-146.k · venues carry no country: same place hint as "check country", set only when not Kenya.
                                 if (rec && !rec.country) {
                                   client.get(`/admin/queue/venue-country?label=${encodeURIComponent(`${rec.label} ${rec.sub || ""}`)}`)
                                     .then((r) => {
                                       const c = r.data.country;
                                       if (c) setDraft((d) => ({ ...d, country: COUNTRY_OPTIONS.some((o) => o.code === c) ? c : "OTHER" }));
                                     }).catch(() => {});
                                 }
                               }} />
              </div>
              <input data-testid={QUEUE.fieldVenueDisplay} placeholder="…or type venue as displayed (fallback)"
                className="mt-2 w-full rounded-md border border-neutral-300 p-2 text-sm"
                value={draft.venue_display} onChange={(e) => setDraft({ ...draft, venue_display: e.target.value })} />
            </div>

            {/* W-B.R26.1 · country (ISO alpha-2). Default Kenya. Any
                other value holds the row out of Approve and tags it
                "outside Kenya · held" on the card + detail chip. */}
            <div className="mt-4" data-testid="field-country-row">
              <label className="label-tiny">country (default Kenya · non-Kenya rows are held for the special tab)</label>
              <select data-testid="field-country"
                      value={(draft.country || "KE").toUpperCase()}
                      onChange={(e) => setDraft({ ...draft, country: e.target.value })}
                      className="mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm">
                {COUNTRY_OPTIONS.map((o) => (
                  <option key={o.code} value={o.code}>{o.label}</option>
                ))}
              </select>
              {(String(draft.country || "KE").toUpperCase() !== "KE") && (
                <p data-testid="country-held-chip-detail"
                   className="mt-2 inline-block rounded-full border px-2 py-0.5 text-[11px] font-semibold lowercase tracking-[0.14em]"
                   style={{ background: "#FDE7E7", color: "#7A1A1A", borderColor: "#9A2222" }}>
                  outside Kenya · held
                </p>
              )}
              {(String(draft.country || "KE").toUpperCase() !== "KE") && (
                <SafariDetailsPanel value={draft.safari} onChange={(v) => setDraft({ ...draft, safari: v })} />
              )}
              {(String(draft.country || "KE").toUpperCase() === "KE") && detail?.country_check_hint && (
                <p data-testid="country-check-chip-detail"
                   className="mt-2 inline-block rounded-full border px-2 py-0.5 text-[11px] font-semibold lowercase tracking-[0.14em]"
                   title={`text mentions ${detail.country_check_hint} · set country if this event is held there`}
                   style={{ background: "#FCE9CF", color: "#7A3C09", borderColor: "#B2550D" }}>
                  check country · {detail.country_check_hint}
                </p>
              )}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3" data-testid="group-money-link">
              <div>
                <label className="label-tiny">Price</label>
                <input data-testid={QUEUE.fieldPrice} className="mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm"
                  placeholder='"Free", "KES 1,500", "1,000–2,500"'
                  value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} />
              </div>
              <div>
                <label className="label-tiny">curator (pick or create)</label>
                <div className="mt-1">
                  <OrganiserCombobox value={draft.organiser_handle || ""}
                                     onChange={(handle) => setDraft({ ...draft, organiser_handle: handle })} />
                </div>
                <CoOrganiserField
                  mainHandle={draft.organiser_handle || ""}
                  value={draft.co_organiser_handles || []}
                  onChange={(list) => setDraft({ ...draft, co_organiser_handles: list })} />
              </div>
            </div>

            <div className="mt-4" data-testid="how-to-get-in-block">
              <label className="label-tiny">How to get in {gates.isPaid && <span className="text-red-600">(paid event · pick a path)</span>}</label>
              <div className="mt-2 flex flex-wrap gap-2" role="radiogroup">
                {[
                  { k: "ticket_link", lbl: "ticket link" },
                  { k: "registration_link", lbl: "registration link" },
                  { k: "contact", lbl: "contact the curator" },
                  { k: "walk_in", lbl: "just show up" },
                ].map(({ k, lbl }) => {
                  const active = (draft.how_to_get_in?.type || "ticket_link") === k;
                  return (
                    <button key={k} type="button" role="radio" aria-checked={active}
                            aria-pressed={active}
                            data-testid={`how-to-get-in-${k}`}
                            onClick={() => setDraft((d) => {
                              const next = { ...(d.how_to_get_in || {}), type: k };
                              // Keep ticket_link in sync when the chosen
                              // type carries a link, so legacy readers
                              // still see something.
                              const newTicket = (k === "ticket_link" || k === "registration_link") ? (next.link || d.ticket_link || "") : "";
                              return { ...d, how_to_get_in: next, ticket_link: newTicket,
                                       registration_required: k === "registration_link" };
                            })}
                            className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${
                              active ? "bg-neutral-900 text-white border-neutral-900"
                                     : "bg-white text-neutral-900 border-neutral-900 hover:bg-neutral-50"
                            }`}>
                      {lbl}
                    </button>
                  );
                })}
              </div>
              {/* link row · for ticket_link and registration_link */}
              {["ticket_link", "registration_link"].includes(draft.how_to_get_in?.type) && (
                <>
                  <input data-testid="field-ticket-link" placeholder="https://…"
                    className="mt-3 w-full rounded-md border border-neutral-300 p-2 text-sm"
                    value={draft.how_to_get_in?.link || draft.ticket_link || ""}
                    onChange={(e) => setDraft((d) => ({
                      ...d, ticket_link: e.target.value, link_status: "",
                      how_to_get_in: { ...(d.how_to_get_in || {}),
                                       type: d.how_to_get_in?.type || "ticket_link",
                                       link: e.target.value },
                    }))} />
                  {detail.link_status && detail.link_status !== "ok" && (
                    <p data-testid="link-broken-flag" className="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: "#B32020" }}>
                      ⚠ link {detail.link_status.replace(/_/g, " ")} · {detail.link_status_reason || "paste a working link"}
                    </p>
                  )}
                </>
              )}
              {/* contact row · for contact */}
              {draft.how_to_get_in?.type === "contact" && (
                <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-3">
                  <input data-testid="field-contact-name" placeholder="name (e.g. Faith)"
                         className="rounded-md border border-neutral-300 p-2 text-sm"
                         value={draft.how_to_get_in?.contact_name || ""}
                         onChange={(e) => setDraft((d) => ({ ...d, how_to_get_in: { ...(d.how_to_get_in || {}), type: "contact", contact_name: e.target.value } }))} />
                  {(() => {
                    const phone = (draft.how_to_get_in?.contact_phone || "").trim();
                    const email = (draft.how_to_get_in?.contact_email || "").trim();
                    // Kenyan phone: +254 7xx..., +254 1xx..., 07xx..., 01xx...
                    const phoneRe = /^(?:\+?254\s*|0)(?:7|1)\d{2}[\s\-.]?\d{3}[\s\-.]?\d{3}$/;
                    const emailRe = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;
                    const phoneBad = !!phone && !phoneRe.test(phone);
                    const emailBad = !!email && !emailRe.test(email);
                    return (
                      <>
                        <div>
                          <input data-testid="field-contact-phone" placeholder="phone · 07xx / 01xx / +254"
                                 className={`w-full rounded-md border p-2 text-sm ${phoneBad ? "border-red-500" : "border-neutral-300"}`}
                                 value={phone}
                                 onChange={(e) => setDraft((d) => ({ ...d, how_to_get_in: { ...(d.how_to_get_in || {}), type: "contact", contact_phone: e.target.value } }))} />
                          {phoneBad && <p data-testid="contact-phone-bad" className="mt-1 text-[11px] text-red-600">kenyan phone format only · 07xx / 01xx / +254</p>}
                        </div>
                        <div>
                          <input data-testid="field-contact-email" placeholder="email"
                                 type="email"
                                 className={`w-full rounded-md border p-2 text-sm ${emailBad ? "border-red-500" : "border-neutral-300"}`}
                                 value={email}
                                 onChange={(e) => setDraft((d) => ({ ...d, how_to_get_in: { ...(d.how_to_get_in || {}), type: "contact", contact_email: e.target.value } }))} />
                          {emailBad && <p data-testid="contact-email-bad" className="mt-1 text-[11px] text-red-600">invalid email</p>}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
              {/* walk-in note · informs Vania the gate only passes on free events */}
              {draft.how_to_get_in?.type === "walk_in" && (
                <p data-testid="walk-in-note" className="mt-2 text-[11px]"
                   style={{ color: gates.isPaid ? "#B32020" : "#1F7A3A" }}>
                  {gates.isPaid
                    ? "just show up only works for free events · the gate stays locked while this is paid"
                    : "no ticket · no form · members tap 'just show up' on the event page"}
                </p>
              )}
            </div>

            <div className="mt-4">
              <label className="label-tiny">main iko</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {IKO_KEYS.map((k) => {
                  const selected = draft.iko === k;
                  // W-B.R5 · D71 (Vania 9 Oct) · text on a bright iko fill
                  // is WHITE. Kili yellow is the one exception · it keeps
                  // night (#1A1A30) text because yellow + white has no
                  // contrast.
                  const fillText = k === "kili" ? "#1A1A30" : "#FFFFFF";
                  return (
                    <button
                      key={k}
                      data-testid={QUEUE.ikoSelector(k)}
                      onClick={() => setDraft({ ...draft, iko: k, iko_supporting: (draft.iko_supporting || []).filter((s) => s !== k) })}
                      className={`rounded-full px-3 py-1 text-sm font-semibold transition-colors ${
                        selected ? "" : "bg-white text-neutral-700 hover:bg-neutral-100"
                      }`}
                      style={selected
                        ? { background: IKO[k].hex, borderColor: IKO[k].hex, color: fillText, border: `1px solid ${IKO[k].hex}` }
                        : { border: "1px solid #d4d4d4" }}
                    >{IKO[k].label.toLowerCase()}</button>
                  );
                })}
              </div>
            </div>
            {/* W-B.R5 (Vania 9 Oct · DL-17 v2) · supporting iko toggles.
                Up to 2 picks. The main iko is disabled here. Selected =
                15% iko tint with iko outline and ink text. */}
            <div className="mt-3" data-testid="iko-supporting-row">
              <label className="label-tiny">also shows under (up to 2)</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {IKO_KEYS.map((k) => {
                  const supporting = draft.iko_supporting || [];
                  const isMain = draft.iko === k;
                  const selected = supporting.includes(k);
                  const disabled = isMain || (!selected && supporting.length >= 2);
                  const toggle = () => {
                    if (disabled) return;
                    setDraft({ ...draft,
                      iko_supporting: selected
                        ? supporting.filter((s) => s !== k)
                        : [...supporting, k].slice(0, 2),
                    });
                  };
                  const hex = IKO[k].hex;
                  const style = selected
                    ? { background: `${hex}26`, border: `1px solid ${hex}`, color: "#1A1A30" }
                    : { border: "1px solid #d4d4d4", background: "#ffffff", color: disabled ? "#9ca3af" : "#1A1A30" };
                  return (
                    <button
                      key={k}
                      type="button"
                      disabled={disabled}
                      data-testid={`iko-supporting-${k}`}
                      onClick={toggle}
                      className="rounded-full px-3 py-1 text-sm font-semibold transition-colors disabled:cursor-not-allowed"
                      style={style}
                    >{IKO[k].label.toLowerCase()}</button>
                  );
                })}
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between">
                <label className="label-tiny">description (≥40 words to publish; the AI can draft from source facts)</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="regenerate-description-btn"
                    onClick={regenerateDescription}
                    disabled={saving}
                    className="rounded-full border border-neutral-300 px-2.5 py-0.5 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-100 disabled:opacity-50"
                  >Regenerate with AI</button>
                  <span data-testid={QUEUE.wordCount} className={`text-xs font-semibold ${gates.description ? "text-green-700" : "text-red-600"}`}>
                    {gates.wordCount} words
                  </span>
                </div>
              </div>
              <textarea data-testid={QUEUE.fieldDescription} rows={7} className="mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm"
                value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            </div>

            <ChainField eventId={detail?.id} chainId={detail?.chain_id} onChanged={() => loadDetail(detail?.id)} />

            <div className="mt-4" data-testid="group-cover-text">
              <label className="label-tiny">Cover image URL (16:9, ≥1920×1080)</label>
              <input data-testid={QUEUE.fieldCover} placeholder="https://…" className="mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm"
                value={draft.cover_image_url} onChange={(e) => {
                  // Kagz Msg 236 #2 · stash the original the first time we replace it,
                  // so a "restore original" always brings it back.
                  setDraft((d) => ({
                    ...d,
                    cover_image_url: e.target.value,
                    original_cover_image_url: d.original_cover_image_url || detail.cover_image_url || "",
                  }));
                }} />
              {draft.cover_image_url && (
                <PastedCoverPreview url={draft.cover_image_url} testid="draft-cover" onState={(s) => setDraftCoverFailed(s === "failed")} className="mt-2 aspect-video w-full rounded-md object-cover" />
              )}
              {draft.original_cover_image_url && draft.original_cover_image_url !== draft.cover_image_url && (
                <div className="mt-2 flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-2 text-[11px]">
                  <span className="text-neutral-500">original poster stored</span>
                  <button type="button" data-testid="restore-original-cover"
                          onClick={() => setDraft((d) => ({ ...d, cover_image_url: d.original_cover_image_url }))}
                          className="ml-auto rounded-full border border-neutral-300 px-2.5 py-0.5 text-[11px] font-semibold hover:bg-white">
                    restore original
                  </button>
                </div>
              )}
              {/* Wave 18b.5 (D62) · swap cover picker. Shows sibling images
                  from `extra_image_urls` + the originals in `poster_classifications`
                  so staff can pick any merged-group file as the cover.
                  Writes via POST /api/admin/drive-watcher/swap-cover/{id}. */}
              {/* WW-178 · always rendered, even when the cover fails or has no siblings. */}
              {(() => {
                const swapUrls = [...new Set([
                  ...(detail.extra_image_urls || []),
                  ...((detail.poster_classifications || []).map((c) => c.image_url).filter(Boolean)),
                ].filter((u) => u && u !== draft.cover_image_url))].slice(0, 8);
                return (
                <div className="mt-3">
                  <label className="label-tiny">swap cover (merged-group siblings)</label>
                  <div className="mt-1 grid grid-cols-4 gap-2" data-testid="swap-cover-picker">
                    {swapUrls.length === 0 && (
                      <p data-testid="swap-cover-empty" className="col-span-4 text-[11px] text-neutral-500">
                        no other images for this event · use edit poster to upload one
                      </p>
                    )}
                    {swapUrls.map((url) => (
                      <button type="button" key={url}
                              data-testid={`swap-cover-${encodeURIComponent(url)}`}
                              onClick={async () => {
                                try {
                                  await client.post(
                                    `/admin/drive-watcher/swap-cover/${encodeURIComponent(detail.id)}`,
                                    null, { params: { image_url: url } },
                                  );
                                  setDraft((d) => ({
                                    ...d, cover_image_url: url,
                                    original_cover_image_url: d.original_cover_image_url || detail.cover_image_url || "",
                                  }));
                                } catch (e) { console.warn("swap cover failed", e); }
                              }}
                              className="aspect-square overflow-hidden rounded-md border border-neutral-300 hover:border-neutral-900">
                        <img src={coverSrc(url)} alt="" className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
                );
              })()}
            </div>

            {/* Gate summary + action bar — Msg 234 URGENT · `sticky bottom-0`
                inside the scrollable form column (was `fixed bottom-0 left-64`
                which anchored to the viewport and assumed the old 256px side-
                bar). Now it stays reachable at every height. */}
          </div>
          <div data-testid="review-details-footer" className="sticky bottom-0 z-30 border-t px-6 py-3" style={{ borderColor: "#E4D7C0", background: "#FFFFFF" }}>
              {/* WW-151 (Vania 13 Oct) · "ready to publish" NEVER appears
                  unless the server's gates_pass is true. When it's false,
                  every failing gate is listed in plain words so Vania can
                  read the real blocker without guessing. */}
              <div className="mb-2 text-[13px]" style={{ fontFamily: "var(--f-body)" }}>
                {detail?.gates_pass ? (
                  <span className="font-semibold" style={{ color: "#0E8148" }}>
                    ✓ ready to publish — a human still approves.
                  </span>
                ) : (
                  <span data-testid="approve-blocked-list" style={{ color: "#B32020" }}>
                    <span className="font-semibold">cannot publish:</span>{" "}
                    {approveBlockedReasons.length
                      ? approveBlockedReasons.join(" · ")
                      : "working on it…"}
                    {detail?.gates?.country_suspect ? (
                      <button type="button" data-testid="confirm-kenya-btn" className="ml-2 underline font-semibold"
                              onClick={async () => {
                                if (!(await confirmDialog({ title: "is this in Kenya?", message: `this looks like it is in ${detail.gates.country_suspect}. confirm only if the event really takes place in Kenya.`, confirmLabel: "it is in Kenya" }))) return;
                                try { await client.post(`/admin/queue/${encodeURIComponent(detail.id)}/confirm-kenya`); toast.success("marked as Kenya"); await loadDetail(detail.id); }
                                catch { toast.error("could not confirm"); }
                              }}>
                        confirm Kenya
                      </button>
                    ) : null}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                {/* R18 FAIL 2 (c) · visible blocked reason + link.
                    WW-150 (Vania 13 Oct) · "open editor" only renders
                    when the row has a linked drive screenshot. For
                    link-only events the inline poster buttons in the
                    details panel (above) carry the whole poster flow. */}
                {detail?.poster_gate && !detail.poster_gate.passes && (
                  <div data-testid="approve-blocked-reason"
                       className="rounded-md border px-3 py-2 text-[12px]"
                       style={{ borderColor: "#B2550D", background: "#FCE9CF", color: "#7A3C09" }}>
                    <span className="font-semibold">{detail.poster_gate.label}</span>
                    {detail.poster_gate.reasons?.length ? <> · {detail.poster_gate.reasons.join(" · ")}</> : null}
                    {(() => {
                      const hasDrive = !!(
                        (detail.raw_inputs?.[0]?.source_metadata?.drive_file_ids || [])[0]
                        || (detail.merged_drive_file_ids || [])[0]
                        || (detail.poster_classifications || [])[0]?.drive_file_id
                      );
                      if (!hasDrive) return null;
                      return (
                        <button type="button" data-testid="approve-blocked-open-editor"
                                onClick={() => { setEditorTab(detail.poster_gate.state === "no_poster" ? "replace" : "crop"); setEditorOpen(true); }}
                                className="ml-2 underline"
                                style={{ color: "#7A3C09" }}>open editor</button>
                      );
                    })()}
                  </div>
                )}
                <button data-testid={QUEUE.approveButton} onClick={approve} disabled={!canApprove || saving || !(detail && detail.id === selectedId && !detailLoading)}
                  title={!(detail && detail.id === selectedId && !detailLoading) ? "loading selected event…" : approveBlockedReason}
                  className="w-full rounded-full bg-neutral-900 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-neutral-700 disabled:cursor-not-allowed disabled:opacity-40">
                  {saving ? "…" : "Approve & Publish"}
                </button>
                <div className="flex items-center gap-3">
                  <button data-testid={QUEUE.saveButton} onClick={save} disabled={saving || !(detail && detail.id === selectedId && !detailLoading)} title={!(detail && detail.id === selectedId && !detailLoading) ? "loading selected event…" : "save draft"} className="flex-1 rounded-full border border-neutral-300 px-4 py-2 text-sm font-semibold hover:bg-neutral-100 disabled:opacity-50">Save Draft</button>
                  <button data-testid={QUEUE.rejectButton} onClick={reject} disabled={saving || !(detail && detail.id === selectedId && !detailLoading)} title={!(detail && detail.id === selectedId && !detailLoading) ? "loading selected event…" : "reject"} className="flex-1 rounded-full border border-red-300 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50">Reject</button>
                </div>
              </div>
          </div>
          </div>
        </div>
      ) : (
        !isMobile && (
          <div data-testid="review-empty-state" className="order-2 flex flex-1 items-center justify-center" style={{ background: "#FBF4E9" }}>
            <p className="text-sm" style={{ color: "#5C5548" }}>pick an item from the queue to review.</p>
          </div>
        )
      )}
      </div>

      {/* Merge side-by-side view — founder Msg 71 Part 6 */}
      {mergeView && (
        <div
          role="dialog"
          aria-modal
          className="fixed inset-0 z-[70] grid place-items-center bg-black/70 p-4"
          onClick={closeMergeView}
          data-testid="merge-view-overlay"
        >
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="label-tiny">duplicate merge · pick winner</p>
              <button onClick={closeMergeView} className="text-xs underline">close (esc)</button>
            </div>
            <h2 className="mt-1 font-display text-xl font-bold">this row vs the earlier one</h2>
            <table className="mt-4 w-full text-xs">
              <thead>
                <tr className="border-b border-neutral-200 text-left">
                  <th className="py-2 pr-3">field</th>
                  <th className="py-2 pr-3">this row (child)</th>
                  <th className="py-2 pr-3">earlier row (parent)</th>
                </tr>
              </thead>
              <tbody>
                {mergeView.diffs.map((d) => (
                  <tr key={d.field} className={`border-b border-neutral-100 align-top ${d.same ? "" : "bg-amber-50"}`}>
                    <td className="py-2 pr-3 font-semibold uppercase tracking-[0.14em] text-[10px] text-neutral-500">{d.field}</td>
                    <td className="py-2 pr-3 whitespace-pre-wrap break-words">{String(d.child ?? "").slice(0, 400) || "—"}</td>
                    <td className="py-2 pr-3 whitespace-pre-wrap break-words">{String(d.parent ?? "").slice(0, 400) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={async () => {
                  await client.post(`/admin/queue/${selectedId}/merge`, {
                    keep_id: mergeView.parent?.id,
                    discard_id: selectedId,
                  });
                  toast.success("kept the earlier row");
                  closeMergeView(); clearSelection(); await loadList();
                }}
                className="rounded-full border border-neutral-900 px-4 py-2 text-sm font-semibold"
              >keep earlier · discard this</button>
              <button
                onClick={async () => {
                  if (!mergeView.parent) return;
                  await client.post(`/admin/queue/${selectedId}/merge`, {
                    keep_id: selectedId,
                    discard_id: mergeView.parent.id,
                  });
                  toast.success("kept this row");
                  closeMergeView(); await loadList();
                }}
                className="rounded-full bg-neutral-900 px-4 py-2 text-sm font-semibold text-white"
              >keep this · discard earlier</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Wave 10B (Vania 1 Oct) · co-organiser multi-picker. Reuses OrganiserCombobox
 *  for search + create. Shows chips with × for each co-organiser. The main
 *  organiser is excluded from chips (deduped). Normalises to lowercase handle
 *  without leading '@'. */
function CoOrganiserField({ mainHandle, value, onChange }) {
  const [adding, setAdding] = React.useState(false);
  const main = (mainHandle || "").trim().replace(/^@+/, "").toLowerCase();
  const chips = (value || []).map((h) => (h || "").trim().replace(/^@+/, "").toLowerCase()).filter(Boolean);
  const remove = (h) => onChange(chips.filter((x) => x !== h));
  const add = (h) => {
    const clean = (h || "").trim().replace(/^@+/, "").toLowerCase();
    if (!clean || clean === main || chips.includes(clean)) { setAdding(false); return; }
    onChange([...chips, clean]);
    setAdding(false);
  };
  return (
    <div className="mt-2" data-testid="co-organisers-field">
      <p className="label-tiny mt-2">co-curators (optional)</p>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {chips.map((h) => (
          <span key={h} data-testid={`co-org-chip-${h}`}
                className="inline-flex items-center gap-1 rounded-full border border-neutral-300 bg-white px-2 py-1 text-xs">
            @{h}
            <button type="button" onClick={() => remove(h)}
                    data-testid={`co-org-remove-${h}`}
                    className="ml-1 text-neutral-500 hover:text-red-600">×</button>
          </span>
        ))}
        {!adding && (
          <button type="button" onClick={() => setAdding(true)}
                  data-testid="co-org-add-btn"
                  className="rounded-full border border-dashed border-neutral-400 px-3 py-1 text-xs font-semibold text-neutral-700 hover:bg-neutral-100">
            + add co-curator
          </button>
        )}
      </div>
      {adding && (
        <div className="mt-2 max-w-md">
          <OrganiserCombobox value=""
                             onChange={add}
                             testId="co-org-combobox" />
          <button type="button" onClick={() => setAdding(false)}
                  className="mt-1 text-[11px] text-neutral-500 hover:underline">cancel</button>
        </div>
      )}
    </div>
  );
}
